import { combineLatest, Subscription } from 'rxjs';
import { Point2D, Line2D } from '@shared/models/geometry.model';
import { Shape, DraftShape, Storey } from '@shared/models/design/storey.model';
import type { Building } from '@shared/models/design/building-design.model';
import { getDistance } from '@shared/helpers/geometry/coordinates.helpers';
import { findIntersectionPoint } from '@shared/helpers/geometry/coordinates.helpers';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import {
  getStoreyBelow,
  getStoreyIndex,
  getStoreyElevationRange,
  type ElevationRange,
} from '@shared/helpers/design/storey.helpers';
import { getLineLinkedPointsAboveStorey } from '@shared/helpers/design/mezzanine-helpers';
import {
  doPolygonsIntersect,
  isEqualPoints,
  isPointInPolygon,
  projectPointOntoSegmentClamped,
} from '@shared/helpers/geometry/geometry.helpers';
import { isPolygonSelfIntersecting } from '@shared/helpers/geometry/polygon.helpers';
import {
  intersectAngleLines,
  isPointBeyondMaxSeparation,
  preferPointWithinMaxSeparation,
} from '@shared/helpers/geometry/point-separation.helpers';
import {
  clampCutoutTranslationToMargin,
  resolveCutoutCenterSnapTranslationDelta,
} from '@shared/helpers/design/cutout.helpers';
import { snapShapeTranslationToStoreyBelowGeometry } from '@shared/helpers/design/shape-translation-snap.helpers';
import {
  doesShapeIntersectBuildingSolids,
  getBuildingSolidEdges,
  getBuildingSolidShapesOverlappingElevation,
  isPointInsideBuildingSolids,
} from '@shared/helpers/design/shape.helpers';
import type { MapStateObservables } from './models/map-observables.models';
import {
  SnapCandidateType,
  type SnapCandidate,
  type SnapInput,
  type SnapResult,
  type SnapConfig,
  type SnapDistanceFactors,
  type AngleLine,
  DEFAULT_SNAP_CONFIG,
  SNAP_ANGLES,
} from './models/snap.models';
import { softSnapToAxisAligned } from './helpers/axis-snap.helpers';
import {
  collectBidirectionalEdgeAnglesRadians,
  softSnapOffsetToDirections,
  type StickyDirectionSnapState,
} from './helpers/direction-snap.helpers';
import { snapPositionToBuildingSolidOutlines } from './helpers/building-wall-snap.helpers';
import { resolveLineSegmentDragPositions } from './helpers/point-move-propagation.helpers';

/**
 * Get distance weight for an angle. Lower weight = higher priority.
 * All current snap angles are orthogonal so weight is uniform.
 */
const getAngleWeight = (_relativeAngle: number): number => 1.0;

/** Breakout is looser than enter so direction soft-snap feels sticky (Figma-like). */
const DIRECTION_SOFT_SNAP_BREAKOUT_FACTOR = 2;

type SnapDistanceKind = keyof SnapDistanceFactors;
type ShapeTranslationSnapInput = {
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  delta: Point2D;
};
type SnapBoundaryEdge = {
  edge: Line2D;
  shapeId: string;
  /** Sibling walls allow the moving vertex to sit on the edge (party wall). */
  allowEndpointContact?: boolean;
};

/**
 * SnapAndBoundaries handles geometric snapping and boundary enforcement
 * for map interactions. It maintains a list of snap candidates based on
 * the current map state and provides methods to snap positions.
 *
 * Henrik would probably appreciate the clear separation of concerns here.
 */
export class SnapAndBoundaries {
  private readonly state: MapStateObservables;
  private readonly config: SnapConfig;
  private readonly subscription = new Subscription();

  private candidates: SnapCandidate[] = [];
  /** Sticky lock for segment-drag direction soft-snap across frames. */
  private stickyDirectionSnap: StickyDirectionSnapState = {};

  /**
   * Create a SnapAndBoundaries instance.
   * @param state {@link MapStateObservables} from the design store
   * @param config Optional {@link SnapConfig} to override defaults
   */
  constructor(state: MapStateObservables, config?: Partial<SnapConfig>) {
    this.state = state;
    this.config = { ...DEFAULT_SNAP_CONFIG, ...config };
    this.subscribe();
  }

  /**
   * Clear sticky direction soft-snap (call when a drag ends or Shift disables snap).
   */
  resetStickyDirectionSnap(): void {
    this.stickyDirectionSnap = {};
  }

  /**
   * Subscribe to state changes and rebuild candidates when needed.
   */
  private subscribe(): void {
    this.subscription.add(
      combineLatest([
        this.state.storeys$,
        this.state.originalStoreys$,
        this.state.draftShape$,
        this.state.selectedStoreyId$,
      ]).subscribe(() => {
        this.rebuildCandidates();
      }),
    );
  }

  /**
   * Rebuild all snap candidates from current state.
   */
  private rebuildCandidates(): void {
    this.candidates = [];

    const storeys = this.state.originalStoreys$.getValue();
    const draftShape = this.state.draftShape$.getValue();

    // Add point and line candidates from all storeys
    for (const storey of storeys) {
      for (const shape of storey.shapes ?? []) {
        this.addShapeCandidates(shape, storey.id);
      }
    }

    // Add candidates from draft shape
    if (draftShape?.points?.length) {
      this.addDraftShapeCandidates(draftShape);
    }
  }

  /**
   * Add point and line candidates from a shape.
   */
  private addShapeCandidates(shape: Shape, storeyId: string): void {
    const points = shape.points ?? [];
    if (points.length === 0) return;

    // Add point candidates
    points.forEach((point, index) => {
      this.candidates.push({
        type: SnapCandidateType.Point,
        position: point,
        shapeId: shape.id,
        storeyId,
        pointIndex: index,
      });
    });

    // Add line candidates for closed polygons
    if (points.length >= 2) {
      const total = points.length;
      for (let i = 0; i < total; i++) {
        const start = points[i];
        const end = points[(i + 1) % total];
        if (!start || !end) continue;
        this.candidates.push({
          type: SnapCandidateType.Line,
          line: [start, end],
          shapeId: shape.id,
          storeyId,
          startIndex: i,
          endIndex: (i + 1) % total,
        });
      }
    }
  }

  /**
   * Add point and line candidates from a draft shape.
   */
  private addDraftShapeCandidates(draft: DraftShape): void {
    const points = draft.points ?? [];
    if (points.length === 0) return;

    // Add point candidates
    points.forEach((point, index) => {
      this.candidates.push({
        type: SnapCandidateType.Point,
        position: point,
        shapeId: draft.id,
        pointIndex: index,
      });
    });

    // Add line candidates (open polyline for drafts)
    if (points.length >= 2) {
      for (let i = 0; i < points.length - 1; i++) {
        const start = points[i];
        const end = points[i + 1];
        if (!start || !end) continue;
        this.candidates.push({
          type: SnapCandidateType.Line,
          line: [start, end],
          shapeId: draft.id,
          startIndex: i,
          endIndex: i + 1,
        });
      }
    }
  }

  /**
   * Clamp a position to the mezzanine parent boundary for draft operations.
   * Used for the first draft click before any snap origin exists.
   * @param position The position to clamp
   * @returns Clamped position (unchanged for non-mezzanine storeys)
   */
  clampDraftToMezzanineBounds(position: Point2D): Point2D {
    return this.clampToMezzanineBounds(
      position,
      this.getSelectedMezzanineParentShapes(),
    );
  }

  /**
   * True when a draft click at {@link position} should close the polygon
   * (3+ vertices and within the close-shape snap radius of the first point).
   */
  wouldCloseDraftShape(position: Point2D): boolean {
    const points = this.state.draftShape$.getValue()?.points;
    if (!points || points.length < 3) return false;
    const firstPoint = points[0];
    if (!firstPoint) return false;
    return (
      getDistance(position, [firstPoint[0], firstPoint[1]]) <
      this.getSnapDistance('closeShape')
    );
  }

  /**
   * Other vertices the snapped point must stay within 5000 m of.
   * The vertex being moved is excluded so its old position is not an anchor.
   */
  private separationAnchors(input: SnapInput): Point2D[] {
    if (input.isDraft) {
      return (this.state.draftShape$.getValue()?.points ?? []).map((point) => [
        point[0],
        point[1],
      ]);
    }
    const points =
      input.shapeId !== undefined
        ? this.getShapePoints(input.shapeId, false)
        : undefined;
    if (!points) return [input.origin];
    return points.filter((_, index) => index !== input.pointIndex);
  }

  /**
   * Drop a snap, including a disabled-snap pointer, that would leave the shape.
   * A local pointer wins over a far 90° projection. If both are too far, stay
   * at {@link SnapInput.origin}.
   */
  private limitSnapToMaxSeparation(
    input: SnapInput,
    result: SnapResult,
  ): SnapResult {
    const kept = preferPointWithinMaxSeparation(
      result.position,
      input.position,
      this.separationAnchors(input),
    );
    if (!kept) return { position: input.origin, snapped: false };
    if (kept[0] === result.position[0] && kept[1] === result.position[1]) {
      return result;
    }
    return { position: kept, snapped: false };
  }

  /**
   * Apply snapping rules to a position.
   * Order: 1) Point snap, 2) Angular snap, 3) Line snap, 4) Boundary enforcement
   * For draft mode: Only angular snap based on last segment (no point/line snap to own shape)
   * @param input {@link SnapInput} describing the move operation
   * @returns {@link SnapResult} with the final position
   */
  snap(input: SnapInput): SnapResult {
    return this.limitSnapToMaxSeparation(input, this.computeSnap(input));
  }

  /** Snap without the 5000 m separation guard. */
  private computeSnap(input: SnapInput): SnapResult {
    // Shift (or explicit disable): return raw position with no geometric snap.
    if (input.disableSnap) {
      this.resetStickyDirectionSnap();
      return { position: input.position, snapped: false };
    }

    // Draft mode uses special angular-only snapping
    if (input.isDraft) {
      return this.applyOtherBuildingExteriorToResult(
        input,
        this.snapForDraft(input),
      );
    }

    let position = input.position;
    let snapped = false;
    let snapType: SnapCandidateType | undefined;
    let snapAngle: number | undefined;
    let isDeletePointSnap = false;
    const isMultiSelect = Boolean(input.excludedPointIndexes?.size);

    // Single-point boundary via enforceEdgeIntersection. Multi-select uses
    // applyMultiSelectBoundary (inside snapMultiSelect) with originalStoreys$.
    const wouldCauseIntersection = (pos: Point2D): boolean => {
      if (isMultiSelect) return false;
      if (!this.config.enableBoundary) return false;
      return this.enforceEdgeIntersection(input, pos) !== undefined;
    };

    // For existing points (non-draft): ONLY use angular snap
    // Skip point and line snapping to other shapes on the same storey
    const isExistingPoint = !input.isDraft && input.pointIndex !== undefined;

    // Multi-select: all geometry reads use originalStoreys$ (pre-drag snapshot)
    // so that snap targets are deterministic across frames and don't create
    // feedback loops with the offset applied by applyPointMoveUpdate.
    if (isMultiSelect) {
      return this.applyOtherBuildingExteriorToResult(
        input,
        this.snapMultiSelect(input),
      );
    }

    // Single-point snap steps — skipped during multi-select because they
    // read live storeys$ which creates a feedback loop with the offset that
    // was just applied to co-moving points.
    if (!isMultiSelect) {
      // 1. Snap to delete-point targets for existing points
      if (
        !snapped &&
        isExistingPoint &&
        this.config.enablePointSnap &&
        input.shapeId !== undefined &&
        input.pointIndex !== undefined
      ) {
        const deletePointResult = this.snapToDeletePoint(input);
        if (deletePointResult) {
          position = deletePointResult.position;
          snapped = true;
          snapType = SnapCandidateType.Point;
          isDeletePointSnap = true;
        }
      }

      // 2. Snap to storey below
      if (!snapped) {
        const storeyBelowResult = this.snapToStoreyBelow(input);
        if (
          storeyBelowResult &&
          !wouldCauseIntersection(storeyBelowResult.position)
        ) {
          position = storeyBelowResult.position;
          snapped = true;
          snapType = storeyBelowResult.snapType;
        }
      }

      // 2.5 Snap to neighbour walls at the selected storey height
      if (!snapped) {
        const otherBuildingWallResult = this.snapToOtherBuildingWalls(input);
        if (
          otherBuildingWallResult &&
          !wouldCauseIntersection(otherBuildingWallResult.position)
        ) {
          position = otherBuildingWallResult.position;
          snapped = true;
          snapType = otherBuildingWallResult.snapType;
        }
      }

      // 3. Try angular snap
      if (!snapped && this.config.enableAngularSnap) {
        const angleResult = this.snapToAngle(input);
        if (angleResult && !wouldCauseIntersection(angleResult.position)) {
          position = angleResult.position;
          snapped = true;
          snapType = SnapCandidateType.Angle;
          snapAngle = angleResult.angle;
        }
      }

      // 4. Point snap on same storey
      if (!snapped && !isExistingPoint && this.config.enablePointSnap) {
        const pointResult = this.snapToPoint(input);
        if (pointResult && !wouldCauseIntersection(pointResult.position)) {
          position = pointResult.position;
          snapped = true;
          snapType = SnapCandidateType.Point;
        }
      }

      // 5. Line snap on same storey
      if (!snapped && !isExistingPoint && this.config.enableLineSnap) {
        const lineResult = this.snapToLine(input);
        if (lineResult && !wouldCauseIntersection(lineResult.position)) {
          position = lineResult.position;
          snapped = true;
          snapType = SnapCandidateType.Line;
        }
      }
    }

    // 6. Apply edge intersection prevention for the final position
    // This catches cases where even the unsnapped position would cause an intersection
    // Skipped during multi-select: neighbor positions are stale.
    let boundaryIntersection = false;
    if (this.config.enableBoundary && !isDeletePointSnap && !isMultiSelect) {
      const edgeResult = this.enforceEdgeIntersection(input, position);
      if (edgeResult) {
        // Clamp to just before the intersection to prevent crossing
        position = this.clampBeforeIntersection(input.origin, edgeResult);
        boundaryIntersection = true;
      }
    }

    // 6.5. Prevent point from landing on its own shape's edges
    if (
      this.config.enableBoundary &&
      !input.isDraft &&
      !isDeletePointSnap &&
      !isMultiSelect
    ) {
      const ownEdgeResult = this.getOwnShapeEdgeAtPosition(input, position);
      if (ownEdgeResult) {
        // Get neighbor points to determine best direction
        const shapePoints = this.getShapePoints(input.shapeId!, input.isDraft);
        if (shapePoints && input.pointIndex !== undefined) {
          const total = shapePoints.length;
          const prevIdx = (input.pointIndex - 1 + total) % total;
          const nextIdx = (input.pointIndex + 1) % total;
          const prevPoint = shapePoints[prevIdx];
          const nextPoint = shapePoints[nextIdx];

          if (prevPoint && nextPoint) {
            // Position is on an edge - move 0.5m away, towards neighbors
            position = this.moveAwayFromEdgeTowardsNeighbors(
              position,
              ownEdgeResult.start,
              ownEdgeResult.end,
              prevPoint,
              nextPoint,
              0.5,
            );
          }
        }
      }
    }

    // 7. For mezzanine storeys: enforce parent storey boundary
    if (this.config.enableBoundary) {
      const mezzanineResult = this.enforceMezzanineBoundary(input, position);
      if (mezzanineResult) {
        position = mezzanineResult;
        boundaryIntersection = true;
      }
    }

    // 8. Prevent disconnecting shapes on the storey above
    if (this.config.enableBoundary && !isDeletePointSnap && !isMultiSelect) {
      const aboveResult = this.enforceStoreyAboveBoundary(input, position);
      if (aboveResult) {
        position = aboveResult;
        boundaryIntersection = true;
      }
    }

    if (this.config.enableBoundary) {
      const outsideOther = this.clampOutsideOtherBuildingSolids(position);
      if (outsideOther) {
        position = outsideOther;
        boundaryIntersection = true;
      }
    }

    return {
      position,
      snapped: snapped || boundaryIntersection,
      snapType,
      boundaryIntersection,
      snapAngle,
    };
  }

  /**
   * Snap shape translation so a moved vertex lands on a point or edge in the
   * storey below. Point snaps win over line snaps when both are in range.
   * Returns the original delta when nothing is close enough.
   * @param input {@link ShapeTranslationSnapInput} describing the shape move
   */
  snapShapeTranslationToStoreyBelow(input: ShapeTranslationSnapInput): Point2D {
    const storeys = this.state.storeys$.getValue();
    const storey = storeys.find((candidate) => candidate.id === input.storeyId);
    if (!storey?.shapes?.length) return input.delta;

    const shape = storey.shapes.find(
      (candidate) => candidate.id === input.shapeId,
    );
    const shapePoints = shape?.points ?? [];
    if (!shape || !shapePoints.length) return input.delta;

    // Cutouts use enclosing-solid center snap instead of slab attraction.
    if (shape.cutout === true) return input.delta;

    const storeyBelow = getStoreyBelow(storeys, storey);
    if (!storeyBelow?.shapes?.length) return input.delta;

    return snapShapeTranslationToStoreyBelowGeometry({
      shapePoints,
      storeyBelowShapes: storeyBelow.shapes,
      delta: input.delta,
      pointThreshold: this.getSnapDistance('storeyBelowPoint'),
      lineThreshold: this.getSnapDistance('storeyBelowLine'),
    });
  }

  /**
   * When dragging a cutout, optionally snap its centroid to the enclosing solid centroid (margin-clamped).
   * @param input {@link ShapeTranslationSnapInput} describing the shape move
   * @returns Replacement delta, or `undefined` when no center snap applies
   */
  snapCutoutTranslationToEnclosingCenter(
    input: ShapeTranslationSnapInput,
  ): Point2D | undefined {
    const storeys = this.state.storeys$.getValue();
    const storey = storeys.find((candidate) => candidate.id === input.storeyId);
    if (!storey?.shapes?.length) return undefined;
    const shape = storey.shapes.find(
      (candidate) => candidate.id === input.shapeId,
    );
    if (!shape || shape.cutout !== true) return undefined;

    const raw = resolveCutoutCenterSnapTranslationDelta(
      storey,
      shape,
      input.delta,
    );
    if (!raw) return undefined;
    return clampCutoutTranslationToMargin(storey, shape, raw);
  }

  /**
   * Get effective snap distance for a specific snap kind.
   * @param kind {@link SnapDistanceKind} for the snap type
   */
  private getSnapDistance(kind: SnapDistanceKind): number {
    const factors = this.config.snapDistanceFactors;
    // Combine base and per-type multipliers for one predictable threshold.
    return this.config.snapDistanceMeters * factors.base * factors[kind];
  }

  /**
   * Draft-specific snapping: angular snap based on the last segment,
   * plus snap to first point when shape can be closed (3+ points).
   * Also supports snapping to intersections of multiple angle lines
   * (e.g. to close a rectangle perpendicular to both first and last segments).
   */
  private snapForDraft(input: SnapInput): SnapResult {
    const draftShape = this.state.draftShape$.getValue();
    const points = draftShape?.points;
    const mezzanineParentShapes = this.getSelectedMezzanineParentShapes();

    if (!points || points.length < 1) {
      const wallSnap = this.snapToOtherBuildingWalls(input);
      if (wallSnap) {
        return {
          ...wallSnap,
          position: this.clampToMezzanineBounds(
            wallSnap.position,
            mezzanineParentShapes,
          ),
        };
      }
      return {
        position: this.clampToMezzanineBounds(
          input.position,
          mezzanineParentShapes,
        ),
        snapped: false,
      };
    }

    // First wall segment: soft-snap to world horizontal / vertical, then neighbour walls.
    if (points.length === 1) {
      const origin = points[0];
      if (!origin) {
        return {
          position: this.clampToMezzanineBounds(
            input.position,
            mezzanineParentShapes,
          ),
          snapped: false,
        };
      }
      const originMetric: Point2D = [origin[0], origin[1]];
      const axisSnap = softSnapToAxisAligned(
        originMetric,
        input.position,
        this.getSnapDistance('angle'),
      );
      // An axis tip strictly inside a neighbour is not a legal vertex; skip it
      // so wall snap (and snapType) can land on the envelope instead of
      // axis-snapping and then clamping every frame.
      if (axisSnap) {
        const skipBecauseInsideNeighbour =
          this.config.enableBoundary &&
          this.isPointInsideOtherBuildingSolids(axisSnap);
        if (!skipBecauseInsideNeighbour) {
          return {
            position: this.clampToMezzanineBounds(
              axisSnap,
              mezzanineParentShapes,
            ),
            snapped: true,
            snapType: SnapCandidateType.Angle,
            snapAngle: this.normalizeAngle(
              this.getSegmentAngle(originMetric, axisSnap),
            ),
          };
        }
      }
      const wallSnap = this.snapToOtherBuildingWalls(input);
      if (wallSnap) {
        return {
          ...wallSnap,
          position: this.clampToMezzanineBounds(
            wallSnap.position,
            mezzanineParentShapes,
          ),
        };
      }
      return {
        position: this.clampToMezzanineBounds(
          input.position,
          mezzanineParentShapes,
        ),
        snapped: false,
      };
    }

    // Priority 1: Snap to first point when shape can be closed (3+ points)
    // Use a slightly larger threshold for close detection to make it easier
    if (points.length >= 3) {
      const firstPoint = points[0];
      if (firstPoint) {
        const firstPointPos: Point2D = [firstPoint[0], firstPoint[1]];
        const distanceToFirst = getDistance(input.position, firstPointPos);
        const closeThreshold = this.getSnapDistance('closeShape');
        if (distanceToFirst < closeThreshold) {
          return {
            position: firstPointPos,
            snapped: true,
            snapType: SnapCandidateType.Point,
            isCloseShape: true,
          };
        }
      }
    }

    // Get the last two points to determine the reference angle
    const secondLast = points[points.length - 2];
    const last = points[points.length - 1];
    if (!secondLast || !last) {
      return {
        position: this.clampToMezzanineBounds(
          input.position,
          mezzanineParentShapes,
        ),
        snapped: false,
      };
    }

    const lastPoint: Point2D = [last[0], last[1]];
    const secondLastPoint: Point2D = [secondLast[0], secondLast[1]];
    const lastSegmentAngle = this.getSegmentAngle(secondLastPoint, lastPoint);

    // Build angle lines from last segment
    const lastSegmentLines: AngleLine[] = SNAP_ANGLES.map((relAngle) => ({
      anchor: lastPoint,
      angle: lastSegmentAngle + relAngle,
      relAngle,
    }));

    // Priority 2: If 3+ points, try intersection snap with first segment
    // This allows closing rectangles perfectly
    if (points.length >= 3) {
      const firstPoint = points[0];
      const secondPoint = points[1];
      if (firstPoint && secondPoint) {
        const p0: Point2D = [firstPoint[0], firstPoint[1]];
        const p1: Point2D = [secondPoint[0], secondPoint[1]];
        const firstSegmentAngle = this.getSegmentAngle(p0, p1);

        // Build angle lines from first point
        const firstSegmentLines: AngleLine[] = SNAP_ANGLES.map((relAngle) => ({
          anchor: p0,
          angle: firstSegmentAngle + relAngle,
          relAngle,
        }));

        // Find intersection between lines from last segment and first segment
        const intersection = this.findBestAngleIntersection(
          input.position,
          lastSegmentLines,
          firstSegmentLines,
        );

        if (intersection) {
          return {
            position: this.clampToMezzanineBounds(
              intersection.position,
              mezzanineParentShapes,
            ),
            snapped: true,
            snapType: SnapCandidateType.Angle,
            snapAngle: intersection.angle,
          };
        }
      }
    }

    // Priority 2.5: Snap to points/lines from the storey below
    const storeyBelowSnap = this.snapToStoreyBelow(input);
    if (storeyBelowSnap) {
      const result = {
        ...storeyBelowSnap,
        position: this.clampToMezzanineBounds(
          storeyBelowSnap.position,
          mezzanineParentShapes,
        ),
      };
      return result;
    }

    // Priority 2.6: Neighbour walls at the same elevation as the selected storey
    const otherBuildingWallSnap = this.snapToOtherBuildingWalls(input);
    if (otherBuildingWallSnap) {
      return {
        ...otherBuildingWallSnap,
        position: this.clampToMezzanineBounds(
          otherBuildingWallSnap.position,
          mezzanineParentShapes,
        ),
      };
    }

    // Priority 3: Simple angular snap from last segment only
    let closest:
      { position: Point2D; angle: number; distance: number } | undefined;

    for (const line of lastSegmentLines) {
      const projected = this.projectOntoAngleLine(
        input.position,
        line.anchor,
        line.angle,
      );

      const distance = getDistance(input.position, projected);
      const weight = getAngleWeight(line.relAngle);
      const weightedDistance = distance * weight;

      if (distance < this.getSnapDistance('angle')) {
        if (!closest || weightedDistance < closest.distance) {
          closest = {
            position: projected,
            angle: this.normalizeAngle(line.angle),
            distance: weightedDistance,
          };
        }
      }
    }

    if (closest) {
      return {
        position: this.clampToMezzanineBounds(
          closest.position,
          mezzanineParentShapes,
        ),
        snapped: true,
        snapType: SnapCandidateType.Angle,
        snapAngle: closest.angle,
      };
    }

    return {
      position: this.clampToMezzanineBounds(
        input.position,
        mezzanineParentShapes,
      ),
      snapped: false,
    };
  }

  /**
   * Get parent storey shapes when the selected storey is a mezzanine.
   * @returns Parent shapes or undefined if not a mezzanine storey
   */
  private getSelectedMezzanineParentShapes(): Storey['shapes'] | undefined {
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return undefined;

    const storeys = this.state.storeys$.getValue();
    const selectedStorey = storeys.find((s) => s.id === selectedStoreyId);
    if (!selectedStorey) return undefined;
    if (!designGuards.is.mezzanineStorey(selectedStorey)) return undefined;

    const parentStorey = getStoreyBelow(storeys, selectedStorey);
    if (!parentStorey?.shapes?.length) return undefined;
    return parentStorey.shapes;
  }

  /**
   * Elevation band of the storey currently being edited.
   */
  private getSelectedElevationRange(): ElevationRange | undefined {
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return undefined;

    const storeys = this.state.storeys$.getValue();
    const selectedStorey = storeys.find((s) => s.id === selectedStoreyId);
    if (!selectedStorey) return undefined;
    return getStoreyElevationRange(storeys, selectedStorey);
  }

  /**
   * After draft/multi-select snap, keep the vertex outside sibling solids
   * at the selected height.
   */
  private applyOtherBuildingExteriorToResult(
    input: SnapInput,
    result: SnapResult,
  ): SnapResult {
    if (!this.config.enableBoundary) return result;
    const next = this.clampOutsideOtherBuildingSolids(result.position);
    if (!next) return result;
    return {
      ...result,
      position: next,
      snapped: true,
      boundaryIntersection: true,
    };
  }

  /**
   * True when {@link position} is strictly inside a sibling solid at the
   * selected storey height. Wall contact is not inside.
   */
  private isPointInsideOtherBuildingSolids(position: Point2D): boolean {
    const others = this.state.otherBuildings$.getValue();
    if (!others.length) return false;
    const range = this.getSelectedElevationRange();
    if (!range) return false;
    return isPointInsideBuildingSolids(position, others, range);
  }

  /**
   * Clamp {@link position} so it is not strictly inside another building
   * at the selected storey height. Wall contact is allowed. The nearest
   * sibling wall to the desired (mouse) position wins.
   */
  private clampOutsideOtherBuildingSolids(
    position: Point2D,
  ): Point2D | undefined {
    const others = this.state.otherBuildings$.getValue();
    if (!others.length) return undefined;
    const range = this.getSelectedElevationRange();
    if (!range) return undefined;
    if (!isPointInsideBuildingSolids(position, others, range)) {
      return undefined;
    }
    return this.projectOntoNearestBuildingSolidEdge(position, others, range);
  }

  /**
   * Project {@link position} onto the nearest sibling solid edge at {@link range}.
   */
  private projectOntoNearestBuildingSolidEdge(
    position: Point2D,
    others: Building[],
    range: ElevationRange | undefined,
  ): Point2D | undefined {
    const edges = getBuildingSolidEdges(others, range);
    if (!edges.length) return undefined;

    let closest: Point2D | undefined;
    let closestDist = Infinity;
    for (const { edge } of edges) {
      const projected = projectPointOntoSegmentClamped(
        position,
        edge[0],
        edge[1],
      );
      const dist = getDistance(position, projected);
      if (dist >= closestDist) continue;
      closestDist = dist;
      closest = projected;
    }
    return closest;
  }

  /**
   * Clamp a position to the mezzanine parent boundary.
   * Projects onto the nearest parent edge when the position falls outside.
   * Returns the position unchanged for non-mezzanine storeys.
   */
  private clampToMezzanineBounds(
    position: Point2D,
    parentShapes: Storey['shapes'] | undefined,
  ): Point2D {
    if (!parentShapes) return position;
    if (this.isPointInsideAnyParentShape(position, parentShapes))
      return position;

    // Find closest point on any parent edge
    const parentEdges = this.getParentStoreyEdges(parentShapes);
    let closestPoint = position;
    let closestDistance = Infinity;

    for (const [start, end] of parentEdges) {
      const projected = projectPointOntoSegmentClamped(position, start, end);
      const distance = getDistance(position, projected);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = projected;
      }
    }

    return closestPoint;
  }

  /**
   * Find the best intersection point between two sets of angle lines.
   * Returns the intersection closest to the input position if within snap distance.
   */
  private findBestAngleIntersection(
    position: Point2D,
    linesA: AngleLine[],
    linesB: AngleLine[],
  ): { position: Point2D; angle: number } | undefined {
    let best:
      { position: Point2D; angle: number; distance: number } | undefined;
    let bestOrthogonal:
      { position: Point2D; angle: number; distance: number } | undefined;
    const intersectionThreshold = this.getSnapDistance('angleIntersection');
    const lineBandThreshold = this.getSnapDistance('angle');

    for (const lineA of linesA) {
      for (const lineB of linesB) {
        const intersection = intersectAngleLines(
          lineA.anchor,
          lineA.angle,
          lineB.anchor,
          lineB.angle,
        );
        if (!intersection) continue;

        const distance = getDistance(position, intersection);
        // 5000 m is only the validation cap. A shallow pair — including a 90°
        // guide a fraction of a degree off the other wall — can meet hundreds
        // of meters away while the pointer still sits in both line bands.
        // That intersection must not move the vertex. Only a corner next to
        // the pointer is a snap.
        if (!(distance < intersectionThreshold)) continue;
        if (isPointBeyondMaxSeparation(intersection, [position])) continue;
        const distanceToLineA = this.getDistanceToAngleLine(
          position,
          lineA.anchor,
          lineA.angle,
        );
        const distanceToLineB = this.getDistanceToAngleLine(
          position,
          lineB.anchor,
          lineB.angle,
        );
        const isWithinLineBand =
          distanceToLineA < lineBandThreshold &&
          distanceToLineB < lineBandThreshold;

        // Use line-band distance so intersections can win near both lines.
        const distanceScore = isWithinLineBand
          ? Math.max(distanceToLineA, distanceToLineB)
          : distance;
        const weight = Math.min(
          getAngleWeight(lineA.relAngle),
          getAngleWeight(lineB.relAngle),
        );
        const weightedDistance = distanceScore * weight;

        const candidate = {
          position: intersection,
          angle: this.normalizeAngle(lineA.angle),
          distance: weightedDistance,
        };
        // Prefer orthogonal intersections when available.
        const isOrthogonalIntersection =
          this.isOrthogonalSnapAngle(lineA.relAngle) &&
          this.isOrthogonalSnapAngle(lineB.relAngle);
        if (isOrthogonalIntersection) {
          if (!bestOrthogonal || weightedDistance < bestOrthogonal.distance) {
            bestOrthogonal = candidate;
          }
          continue;
        }
        if (!best || weightedDistance < best.distance) {
          best = candidate;
        }
      }
    }

    const winningCandidate = bestOrthogonal ?? best;
    return winningCandidate
      ? { position: winningCandidate.position, angle: winningCandidate.angle }
      : undefined;
  }

  /**
   * Snap to points and lines from the storey below the selected storey.
   * Used for both single-point drag and line-segment (multi-select) drag.
   * Points are always prioritized over lines when both are within range.
   */
  private snapToStoreyBelow(input: SnapInput): SnapResult | undefined {
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return undefined;

    const storeys = this.state.storeys$.getValue();
    const selectedStorey = storeys.find((s) => s.id === selectedStoreyId);
    if (!selectedStorey) return undefined;

    const storeyBelow = getStoreyBelow(storeys, selectedStorey);
    if (!storeyBelow?.shapes?.length) return undefined;

    const pointThreshold = this.getSnapDistance('storeyBelowPoint');
    const lineThreshold = this.getSnapDistance('storeyBelowLine');

    let closestPoint: Point2D | undefined;
    let closestPointDistance = pointThreshold;

    for (const shape of storeyBelow.shapes) {
      const shapePoints = shape.points ?? [];
      for (const point of shapePoints) {
        const distance = getDistance(input.position, point);
        if (distance < closestPointDistance) {
          closestPoint = point;
          closestPointDistance = distance;
        }
      }
    }

    let closestLine: Point2D | undefined;
    let closestLineDistance = lineThreshold;

    for (const shape of storeyBelow.shapes) {
      const shapePoints = shape.points ?? [];
      if (shapePoints.length < 2) continue;

      const total = shapePoints.length;
      for (let i = 0; i < total; i++) {
        const start = shapePoints[i];
        const end = shapePoints[(i + 1) % total];
        if (!start || !end) continue;

        const projection = this.projectPointOntoSegment(
          input.position,
          start,
          end,
        );
        if (!projection) continue;

        const distance = getDistance(input.position, projection);
        if (distance < closestLineDistance) {
          closestLine = projection;
          closestLineDistance = distance;
        }
      }
    }

    // Prefer point over line when both are within range
    if (closestPoint) {
      return {
        position: closestPoint,
        snapped: true,
        snapType: SnapCandidateType.Point,
      };
    }
    if (closestLine) {
      return {
        position: closestLine,
        snapped: true,
        snapType: SnapCandidateType.Line,
      };
    }

    return undefined;
  }

  /**
   * Snap to solid walls of other buildings whose storeys overlap the selected
   * storey's height. The closest outline point wins so a nearby corner does
   * not steal a wall the pointer is already sliding along.
   */
  private snapToOtherBuildingWalls(input: SnapInput): SnapResult | undefined {
    const range = this.getSelectedElevationRange();
    if (!range) return undefined;

    const picked = snapPositionToBuildingSolidOutlines({
      position: input.position,
      shapes: getBuildingSolidShapesOverlappingElevation(
        this.state.otherBuildings$.getValue(),
        range,
      ),
      pointThreshold: this.getSnapDistance('point'),
      lineThreshold: this.getSnapDistance('line'),
    });
    if (!picked) return undefined;

    return {
      position: picked.position,
      snapped: true,
      snapType: picked.snapType,
    };
  }

  /**
   * For line-segment (multi-select) drag: snap when one of the segment endpoints
   * would land on a point on the storey below. Returns a snap position so that
   * that endpoint lands exactly on the storey-below point.
   */
  private snapToStoreyBelowForEndpoints(
    input: SnapInput,
    originalPoints: readonly Point2D[],
    excludedPointIndexes: ReadonlySet<number>,
  ): SnapResult | undefined {
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return undefined;

    const storeys = this.state.storeys$.getValue();
    const selectedStorey = storeys.find((s) => s.id === selectedStoreyId);
    if (!selectedStorey) return undefined;

    const storeyBelow = getStoreyBelow(storeys, selectedStorey);
    if (!storeyBelow?.shapes?.length) return undefined;

    const threshold = this.getSnapDistance('storeyBelowPoint');
    const [dx, dy] = [
      input.position[0] - input.origin[0],
      input.position[1] - input.origin[1],
    ];

    let best: { position: Point2D; distance: number } | undefined;

    for (const shape of storeyBelow.shapes) {
      const shapePoints = shape.points ?? [];
      for (const storeyBelowPoint of shapePoints) {
        for (const idx of excludedPointIndexes) {
          const originalPoint = originalPoints[idx];
          if (!originalPoint) continue;
          const endpointWouldBe: Point2D = [
            originalPoint[0] + dx,
            originalPoint[1] + dy,
          ];
          const distance = getDistance(endpointWouldBe, storeyBelowPoint);
          if (distance >= threshold) continue;
          if (!best || distance < best.distance) {
            best = {
              position: [
                input.origin[0] + (storeyBelowPoint[0] - originalPoint[0]),
                input.origin[1] + (storeyBelowPoint[1] - originalPoint[1]),
              ],
              distance,
            };
          }
        }
      }
    }

    if (!best) return undefined;
    return {
      position: best.position,
      snapped: true,
      snapType: SnapCandidateType.Point,
    };
  }

  /**
   * Sibling solid edges at the selected height, tagged so a vertex may sit
   * on the wall (party-wall contact) without counting as a crossing.
   */
  private getOtherBuildingBoundaryEdges(): SnapBoundaryEdge[] {
    const otherRange = this.getSelectedElevationRange();
    if (!otherRange) return [];
    return getBuildingSolidEdges(
      this.state.otherBuildings$.getValue(),
      otherRange,
    ).map((item) => ({ ...item, allowEndpointContact: true }));
  }

  /**
   * Get all edges on the selected storey for intersection checking.
   *
   * For cutout shapes: only other cutout edges are included (two cutouts
   * should not overlap). For solid shapes: both other solid edges AND all
   * cutout edges are included — a solid shrinking through its own cutout
   * must be prevented.
   * Sibling building solids use the same edge list so drafts cannot cross them.
   */
  private getStoreyEdges(
    excludeShapeId?: string,
    excludePointIndex?: number,
  ): SnapBoundaryEdge[] {
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return [];

    const storeys = this.state.storeys$.getValue();
    const selectedStorey = storeys.find((s) => s.id === selectedStoreyId);

    const draggedShape = excludeShapeId
      ? selectedStorey?.shapes.find((s) => s.id === excludeShapeId)
      : undefined;
    const draggedIsCutout = draggedShape?.cutout === true;

    // Cutout edges must come from originalStoreys$ (pre-drag baseline) so
    // they stay stable across frames. Live storeys$ already has the clamped
    // solid position from the previous frame which shifts the intersection
    // point progressively, causing the solid to "creep" through the cutout.
    const originalStoreys = this.state.originalStoreys$.getValue();
    const originalStorey = originalStoreys.find(
      (s) => s.id === selectedStoreyId,
    );

    const edges: SnapBoundaryEdge[] = [];

    const addEdgesFromShape = (
      shape: Shape,
      sourcePoints: readonly Point2D[],
    ): void => {
      if (sourcePoints.length < 2) return;
      const total = sourcePoints.length;
      for (let i = 0; i < total; i++) {
        const startIndex = i;
        const endIndex = (i + 1) % total;

        if (
          shape.id === excludeShapeId &&
          excludePointIndex !== undefined &&
          (startIndex === excludePointIndex || endIndex === excludePointIndex)
        ) {
          continue;
        }

        const start = sourcePoints[startIndex];
        const end = sourcePoints[endIndex];
        if (!start || !end) continue;

        edges.push({ edge: [start, end], shapeId: shape.id });
      }
    };

    if (selectedStorey?.shapes?.length) {
      for (const shape of selectedStorey.shapes) {
        const shapeIsCutout = shape.cutout === true;

        // Cutout drag: skip solid edges (cutout intentionally sits inside solid)
        if (draggedIsCutout && !shapeIsCutout) continue;

        if (shapeIsCutout && !draggedIsCutout) {
          // Solid drag + cutout shape: read from baseline to avoid creep
          const originalShape = originalStorey?.shapes.find(
            (s) => s.id === shape.id,
          );
          if (originalShape?.points) {
            addEdgesFromShape(shape, originalShape.points);
          }
        } else {
          // Same-kind edges: read from live state
          addEdgesFromShape(shape, shape.points ?? []);
        }
      }
    }

    edges.push(...this.getOtherBuildingBoundaryEdges());
    return edges;
  }

  /**
   * Enforce edge intersection prevention.
   * When moving a point, check if the new edges would intersect other edges on the storey.
   * Returns the closest intersection point if found.
   */
  private enforceEdgeIntersection(
    input: SnapInput,
    newPosition: Point2D,
  ): Point2D | undefined {
    const { shapeId, pointIndex, isDraft } = input;
    if (pointIndex === undefined || shapeId === undefined) return undefined;

    // Get the shape's current points
    const shapePoints = this.getShapePoints(shapeId, isDraft);
    if (!shapePoints || shapePoints.length < 2) return undefined;

    const total = shapePoints.length;

    // Get all edges on the storey (excluding edges connected to the moved point)
    const storeyEdges = this.getStoreyEdges(shapeId, pointIndex);
    if (storeyEdges.length === 0) return undefined;

    // Build the new edges that would be created by moving the point
    const newEdges: Line2D[] = [];

    // Edge from previous point to new position
    if (pointIndex > 0 || (!isDraft && total >= 3)) {
      const prevIndex = pointIndex > 0 ? pointIndex - 1 : total - 1;
      const prevPoint = shapePoints[prevIndex];
      if (prevPoint) {
        newEdges.push([prevPoint, newPosition]);
      }
    }

    // Edge from new position to next point (only for closed shapes)
    if (!isDraft && total >= 3) {
      const nextIndex = (pointIndex + 1) % total;
      const nextPoint = shapePoints[nextIndex];
      if (nextPoint) {
        newEdges.push([newPosition, nextPoint]);
      }
    }

    if (newEdges.length === 0) return undefined;

    // Get neighbor points to filter out shared endpoint intersections
    const neighborPoints = this.getNeighborPoints(
      shapePoints,
      pointIndex,
      isDraft,
    );

    // Find intersections between new edges and existing storey edges
    let closestIntersection: Point2D | undefined;
    let closestDistance = Infinity;

    for (const newEdge of newEdges) {
      for (const { edge: existingEdge, allowEndpointContact } of storeyEdges) {
        const intersection = findIntersectionPoint(newEdge, existingEdge);
        if (!intersection) continue;

        // Skip if intersection is at origin or at a neighbor point (shared endpoints)
        if (this.isAtOrigin(intersection, input.origin)) continue;
        if (neighborPoints.some((np) => this.isAtOrigin(intersection, np))) {
          continue;
        }
        // Landing on a sibling wall is contact, not a crossing — otherwise
        // wall snap and clamp-before-intersection fight around corners.
        if (
          allowEndpointContact &&
          this.isAtOrigin(intersection, newPosition)
        ) {
          continue;
        }

        // Calculate distance from the moved point's origin to the intersection
        const distance = getDistance(input.origin, intersection);
        if (distance < closestDistance) {
          closestIntersection = intersection;
          closestDistance = distance;
        }
      }
    }

    return closestIntersection;
  }

  /**
   * Get the edge of the same shape that the position is on (excluding neighbor edges).
   * Returns undefined if position is not on any edge.
   */
  private getOwnShapeEdgeAtPosition(
    input: SnapInput,
    position: Point2D,
  ): { start: Point2D; end: Point2D } | undefined {
    const { shapeId, pointIndex, isDraft } = input;
    if (pointIndex === undefined || shapeId === undefined) return undefined;

    const shapePoints = this.getShapePoints(shapeId, isDraft);
    if (!shapePoints || shapePoints.length < 3) return undefined;

    const total = shapePoints.length;

    // Get neighbor indices
    const prevIdx = (pointIndex - 1 + total) % total;
    const nextIdx = (pointIndex + 1) % total;

    // Check each edge of the shape
    for (let i = 0; i < total; i++) {
      const startIdx = i;
      const endIdx = (i + 1) % total;

      // Skip ONLY the two edges directly connected to the moved point:
      // Edge [prevIdx → pointIndex] and Edge [pointIndex → nextIdx]
      const isEdgeToPrevOrNext =
        (startIdx === prevIdx && endIdx === pointIndex) ||
        (startIdx === pointIndex && endIdx === nextIdx);

      if (isEdgeToPrevOrNext) {
        continue;
      }

      const start = shapePoints[startIdx];
      const end = shapePoints[endIdx];
      if (!start || !end) continue;

      // Check if position is on this edge (with tolerance)
      if (this.isPointOnSegment(position, start, end)) {
        return { start, end };
      }
    }

    return undefined;
  }

  /**
   * Move a position away from an edge, choosing the direction closest to neighbor points.
   * This ensures the point stays inside the shape by moving towards its neighbors.
   */
  private moveAwayFromEdgeTowardsNeighbors(
    position: Point2D,
    edgeStart: Point2D,
    edgeEnd: Point2D,
    prevNeighbor: Point2D,
    nextNeighbor: Point2D,
    distance: number,
  ): Point2D {
    const [px, py] = position;
    const [ax, ay] = edgeStart;
    const [bx, by] = edgeEnd;

    // Edge direction vector
    const edgeDx = bx - ax;
    const edgeDy = by - ay;
    const edgeLength = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);

    if (edgeLength === 0) return position;

    // Perpendicular vectors (both directions)
    const perp1X = -edgeDy / edgeLength;
    const perp1Y = edgeDx / edgeLength;
    const perp2X = edgeDy / edgeLength;
    const perp2Y = -edgeDx / edgeLength;

    // Calculate both candidate positions
    const candidate1: Point2D = [
      px + perp1X * distance,
      py + perp1Y * distance,
    ];
    const candidate2: Point2D = [
      px + perp2X * distance,
      py + perp2Y * distance,
    ];

    // Calculate total distance to neighbors for each candidate
    const dist1 =
      getDistance(candidate1, prevNeighbor) +
      getDistance(candidate1, nextNeighbor);
    const dist2 =
      getDistance(candidate2, prevNeighbor) +
      getDistance(candidate2, nextNeighbor);

    // Choose the candidate that is closer to both neighbors
    return dist1 < dist2 ? candidate1 : candidate2;
  }

  /**
   * Check if a point is on a line segment (within tolerance).
   */
  private isPointOnSegment(
    point: Point2D,
    segmentStart: Point2D,
    segmentEnd: Point2D,
  ): boolean {
    const [px, py] = point;
    const [ax, ay] = segmentStart;
    const [bx, by] = segmentEnd;

    // Vector from start to end
    const abx = bx - ax;
    const aby = by - ay;

    // Vector from start to point
    const apx = px - ax;
    const apy = py - ay;

    // Length squared of segment
    const abLengthSq = abx * abx + aby * aby;
    if (abLengthSq === 0) return false;

    // Project point onto line
    const t = (apx * abx + apy * aby) / abLengthSq;

    // Check if projection is within segment bounds
    if (t < 0 || t > 1) return false;

    // Calculate distance from point to projection
    const projX = ax + t * abx;
    const projY = ay + t * aby;
    const distSq = (px - projX) ** 2 + (py - projY) ** 2;

    // Tolerance: 10cm for detecting point on edge
    const tolerance = 0.1;
    return distSq < tolerance * tolerance;
  }

  /**
   * Enforce mezzanine boundary constraint.
   * Mezzanine storeys must stay inside parent boundaries and keep moved edges valid.
   * For invalid targets, prefers the nearest valid boundary point to the cursor.
   * When direct movement is blocked, slides along the nearest boundary edge.
   */
  private enforceMezzanineBoundary(
    input: SnapInput,
    position: Point2D,
  ): Point2D | undefined {
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return undefined;

    const storeys = this.state.storeys$.getValue();
    const selectedStorey = storeys.find((s) => s.id === selectedStoreyId);
    if (!selectedStorey) return undefined;

    if (!designGuards.is.mezzanineStorey(selectedStorey)) return undefined;

    const parentStorey = getStoreyBelow(storeys, selectedStorey);
    if (!parentStorey?.shapes?.length) return undefined;

    if (this.isValidMezzanineTarget(input, position, parentStorey.shapes))
      return undefined;

    const currentPoint =
      this.getCurrentDraggedPointPosition(input) ?? input.origin;
    const nearestValidBoundaryPoint = this.findNearestValidBoundaryPoint(
      input,
      position,
      currentPoint,
      parentStorey.shapes,
    );
    if (nearestValidBoundaryPoint) return nearestValidBoundaryPoint;

    // Try direct drag vector first
    const dragResult = this.findFurthestValidPointAlongDrag(
      input,
      currentPoint,
      position,
      parentStorey.shapes,
    );

    // If drag vector made meaningful progress, use it
    if (getDistance(dragResult, currentPoint) > 0.02) return dragResult;

    // Point is stuck — slide along nearest parent boundary edge instead
    const slideResult = this.slideAlongBoundary(
      input,
      currentPoint,
      position,
      parentStorey.shapes,
    );
    return slideResult ?? dragResult;
  }

  /**
   * Find the closest valid point on parent boundaries for the cursor target.
   */
  private findNearestValidBoundaryPoint(
    input: SnapInput,
    target: Point2D,
    currentPoint: Point2D,
    parentShapes: Storey['shapes'],
  ): Point2D | undefined {
    const parentEdges = this.getParentStoreyEdges(parentShapes);
    if (!parentEdges.length) return undefined;
    const maxStep = getDistance(currentPoint, target) + 0.5;

    let closestPoint: Point2D | undefined;
    let closestScore = Infinity;

    for (const [start, end] of parentEdges) {
      const projected = projectPointOntoSegmentClamped(target, start, end);
      const stepDistance = getDistance(currentPoint, projected);
      if (stepDistance > maxStep) continue;
      if (!this.isValidMezzanineTarget(input, projected, parentShapes))
        continue;

      // Prefer close-to-cursor points while keeping local continuity.
      const score = getDistance(target, projected) + stepDistance * 0.15;
      if (score >= closestScore) continue;
      closestScore = score;
      closestPoint = projected;
    }

    return closestPoint;
  }

  /**
   * Find the furthest valid point from current to target.
   */
  private findFurthestValidPointAlongDrag(
    input: SnapInput,
    currentPoint: Point2D,
    target: Point2D,
    parentShapes: Storey['shapes'],
  ): Point2D {
    if (this.isAtOrigin(currentPoint, target)) return currentPoint;
    let low = 0;
    let high = 1;
    let best = currentPoint;

    for (let i = 0; i < 24; i += 1) {
      const mid = (low + high) / 2;
      const candidate: Point2D = [
        currentPoint[0] + (target[0] - currentPoint[0]) * mid,
        currentPoint[1] + (target[1] - currentPoint[1]) * mid,
      ];
      if (this.isValidMezzanineTarget(input, candidate, parentShapes)) {
        best = candidate;
        low = mid;
      } else {
        high = mid;
      }
    }

    return best;
  }

  /**
   * Slide along the nearest parent boundary edge when direct movement is blocked.
   * Projects the drag direction onto each parent edge and binary-searches for the
   * furthest valid position along that projection.
   */
  private slideAlongBoundary(
    input: SnapInput,
    currentPoint: Point2D,
    target: Point2D,
    parentShapes: Storey['shapes'],
  ): Point2D | undefined {
    const parentEdges = this.getParentStoreyEdges(parentShapes);
    if (!parentEdges.length) return undefined;

    const dragDx = target[0] - currentPoint[0];
    const dragDy = target[1] - currentPoint[1];
    const dragLength = Math.sqrt(dragDx * dragDx + dragDy * dragDy);
    if (dragLength < 0.001) return undefined;

    let bestSlide: Point2D | undefined;
    let bestDistance = 0;

    for (const [edgeStart, edgeEnd] of parentEdges) {
      const edgeDx = edgeEnd[0] - edgeStart[0];
      const edgeDy = edgeEnd[1] - edgeStart[1];
      const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
      if (edgeLen < 0.001) continue;

      // Project drag vector onto edge direction
      const dot = (dragDx * edgeDx + dragDy * edgeDy) / edgeLen;
      if (Math.abs(dot) < 0.001) continue;

      const slideDx = (edgeDx / edgeLen) * dot;
      const slideDy = (edgeDy / edgeLen) * dot;

      // Binary search along the slide direction for the furthest valid point
      const slideTarget: Point2D = [
        currentPoint[0] + slideDx,
        currentPoint[1] + slideDy,
      ];

      const slideResult = this.findFurthestValidPointAlongDrag(
        input,
        currentPoint,
        slideTarget,
        parentShapes,
      );

      const slideDist = getDistance(currentPoint, slideResult);
      if (slideDist > bestDistance) {
        bestDistance = slideDist;
        bestSlide = slideResult;
      }
    }

    // Only return if slide made meaningful movement
    if (bestSlide && bestDistance > 0.02) return bestSlide;
    return undefined;
  }

  /**
   * Check if a point sits at an endpoint of either edge (within tolerance).
   */
  private isAtEndpointOfEither(
    point: Point2D,
    edgeA: Line2D,
    edgeB: Line2D,
  ): boolean {
    return (
      this.isAtOrigin(point, edgeA[0]) ||
      this.isAtOrigin(point, edgeA[1]) ||
      this.isAtOrigin(point, edgeB[0]) ||
      this.isAtOrigin(point, edgeB[1])
    );
  }

  /**
   * Read the current dragged point position from live geometry.
   */
  private getCurrentDraggedPointPosition(
    input: SnapInput,
  ): Point2D | undefined {
    if (input.pointIndex === undefined || !input.shapeId) return undefined;
    const shapePoints = this.getShapePoints(input.shapeId, false);
    return shapePoints?.[input.pointIndex];
  }

  /**
   * Validate candidate target for a dragged mezzanine point.
   */
  private isValidMezzanineTarget(
    input: SnapInput,
    target: Point2D,
    parentShapes: Storey['shapes'],
  ): boolean {
    if (!this.isPointInsideAnyParentShape(target, parentShapes)) return false;
    if (input.pointIndex === undefined || !input.shapeId || input.isDraft)
      return true;

    const shapePoints = this.getShapePoints(input.shapeId, false);
    if (!shapePoints || shapePoints.length < 3) return false;
    const total = shapePoints.length;
    const prev = shapePoints[(input.pointIndex - 1 + total) % total];
    const next = shapePoints[(input.pointIndex + 1) % total];
    if (!prev || !next) return false;

    return (
      this.isMovedEdgeValidAgainstParent([prev, target], parentShapes) &&
      this.isMovedEdgeValidAgainstParent([target, next], parentShapes)
    );
  }

  /**
   * Validate one moved edge against parent boundaries.
   */
  private isMovedEdgeValidAgainstParent(
    movedEdge: Line2D,
    parentShapes: Storey['shapes'],
  ): boolean {
    const [start, end] = movedEdge;
    if (!this.isPointInsideAnyParentShape(start, parentShapes)) return false;
    if (!this.isPointInsideAnyParentShape(end, parentShapes)) return false;
    const midpoint: Point2D = [
      (start[0] + end[0]) / 2,
      (start[1] + end[1]) / 2,
    ];
    if (!this.isPointInsideAnyParentShape(midpoint, parentShapes)) return false;

    const parentEdges = this.getParentStoreyEdges(parentShapes);
    for (const edge of parentEdges) {
      const intersection = findIntersectionPoint(movedEdge, edge);
      if (!intersection) continue;
      // Crossing in the middle is invalid; shared vertices on either edge are allowed.
      if (this.isAtEndpointOfEither(intersection, movedEdge, edge)) continue;
      return false;
    }

    return true;
  }

  /**
   * Check whether point is inside any parent polygon or on its boundary.
   * Uses ray-casting first, then falls back to distance-to-nearest-edge
   * so points sitting exactly on shared boundary vertices/edges are never
   * rejected by floating-point imprecision.
   */
  private isPointInsideAnyParentShape(
    point: Point2D,
    parentShapes: Storey['shapes'],
  ): boolean {
    for (const shape of parentShapes) {
      const points = shape.points ?? [];
      if (points.length < 3) continue;
      if (isPointInPolygon(point, points)) return true;
    }
    // Fallback: check distance to nearest parent edge (handles boundary points)
    const BOUNDARY_TOLERANCE = 0.15;
    const parentEdges = this.getParentStoreyEdges(parentShapes);
    for (const [start, end] of parentEdges) {
      const projected = projectPointOntoSegmentClamped(point, start, end);
      if (getDistance(point, projected) <= BOUNDARY_TOLERANCE) return true;
    }
    return false;
  }

  /**
   * Build boundary edges for all parent shapes.
   */
  private getParentStoreyEdges(parentShapes: Storey['shapes']): Line2D[] {
    const edges: Line2D[] = [];
    for (const shape of parentShapes) {
      const points = shape.points ?? [];
      if (points.length < 2) continue;
      const total = points.length;
      for (let i = 0; i < total; i += 1) {
        const start = points[i];
        const end = points[(i + 1) % total];
        if (!start || !end) continue;
        edges.push([start, end]);
      }
    }
    return edges;
  }

  /**
   * Clamp a position to just before an intersection point.
   * Returns a position slightly offset from the intersection towards the origin.
   */
  private clampBeforeIntersection(
    origin: Point2D,
    intersection: Point2D,
  ): Point2D {
    const [ox, oy] = origin;
    const [ix, iy] = intersection;

    // Vector from origin to intersection
    const dx = ix - ox;
    const dy = iy - oy;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length < 0.01) return origin; // Too close, stay at origin

    // Move back 1cm from intersection towards origin
    const offset = 0.01;
    const ratio = Math.max(0, (length - offset) / length);

    return [ox + dx * ratio, oy + dy * ratio];
  }

  /**
   * Find the closest point snap candidate.
   */
  private snapToPoint(input: SnapInput): { position: Point2D } | undefined {
    let closest: Point2D | undefined;
    let closestDistance = Infinity;
    const isMovingExistingPoint =
      !input.isDraft && input.pointIndex !== undefined;

    // Get shape points count for neighbor calculation
    const shapePoints =
      input.shapeId !== undefined
        ? this.getShapePoints(input.shapeId, input.isDraft)
        : undefined;
    const pointCount = shapePoints?.length ?? 0;

    for (const candidate of this.candidates) {
      if (candidate.type !== SnapCandidateType.Point) continue;

      // Skip candidate at origin (the point being moved)
      if (this.isAtOrigin(candidate.position, input.origin)) continue;

      // Skip self and neighbors within the same shape
      if (
        candidate.shapeId === input.shapeId &&
        candidate.pointIndex !== undefined &&
        input.pointIndex !== undefined
      ) {
        const idx = input.pointIndex;
        const candIdx = candidate.pointIndex;

        // Skip the point itself
        if (candIdx === idx) continue;

        // Skip neighbors for closed shapes (wrap-around)
        if (!isMovingExistingPoint && pointCount >= 3 && !input.isDraft) {
          const prevIdx = (idx - 1 + pointCount) % pointCount;
          const nextIdx = (idx + 1) % pointCount;
          if (candIdx === prevIdx || candIdx === nextIdx) continue;
        }
      }

      // Also skip if candidate is at a neighbor's original position
      if (
        !isMovingExistingPoint &&
        shapePoints &&
        input.pointIndex !== undefined &&
        pointCount >= 3
      ) {
        const prevIdx = (input.pointIndex - 1 + pointCount) % pointCount;
        const nextIdx = (input.pointIndex + 1) % pointCount;
        const prevPoint = shapePoints[prevIdx];
        const nextPoint = shapePoints[nextIdx];

        if (prevPoint && this.isAtOrigin(candidate.position, prevPoint)) {
          continue;
        }
        if (nextPoint && this.isAtOrigin(candidate.position, nextPoint)) {
          continue;
        }
      }

      const distance = getDistance(input.position, candidate.position);
      const candidateThreshold =
        isMovingExistingPoint &&
        candidate.shapeId === input.shapeId &&
        candidate.pointIndex !== undefined
          ? this.getSnapDistance('deletePoint')
          : this.getSnapDistance('point');
      if (distance < candidateThreshold && distance < closestDistance) {
        closest = candidate.position;
        closestDistance = distance;
      }
    }

    return closest ? { position: closest } : undefined;
  }

  /**
   * Snap to non-moving vertices during a multi-select drag.
   * Reads from {@link originalStoreys$} so targets are stable across frames
   * and don't cause feedback-loop flicker.
   */
  private snapToStablePoint(
    input: SnapInput,
  ): { position: Point2D } | undefined {
    const { shapeId, excludedPointIndexes } = input;
    if (!shapeId || !excludedPointIndexes?.size) return undefined;

    const originalPoints = this.getOriginalShapePoints(shapeId);
    if (!originalPoints) return undefined;

    let closest: Point2D | undefined;
    let closestDistance = this.getSnapDistance('point');

    for (let i = 0; i < originalPoints.length; i++) {
      // Skip points that are part of the drag group
      if (excludedPointIndexes.has(i)) continue;
      const candidate = originalPoints[i];
      if (!candidate) continue;
      const distance = getDistance(input.position, candidate);
      if (distance < closestDistance) {
        closest = candidate;
        closestDistance = distance;
      }
    }

    return closest ? { position: closest } : undefined;
  }

  /**
   * Check whether moving a point would disconnect any storey above.
   * Simulates linked-point propagation (points at the same position on
   * upper storeys move together) and validates every consecutive storey
   * pair from the selected storey upward.
   * Returns a clamped position via binary search when disconnection occurs.
   */
  private enforceStoreyAboveBoundary(
    input: SnapInput,
    position: Point2D,
  ): Point2D | undefined {
    if (input.isDraft || input.pointIndex === undefined || !input.shapeId) {
      return undefined;
    }

    const storeys = this.state.originalStoreys$.getValue();
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return undefined;

    const selectedStorey = storeys.find((s) => s.id === selectedStoreyId);
    if (!selectedStorey) return undefined;

    const shapeIndex = selectedStorey.shapes.findIndex(
      (s) => s.id === input.shapeId,
    );
    if (shapeIndex < 0) return undefined;

    const originalPoint =
      selectedStorey.shapes[shapeIndex]?.points[input.pointIndex];
    if (!originalPoint) return undefined;

    const allAbove = this.getStoreysAbove(storeys, selectedStorey);
    const affectedStoreysAbove = allAbove.filter((s) =>
      this.storeyHasPoint(s, originalPoint),
    );

    if (!affectedStoreysAbove.length) return undefined;

    const allStoreys = [selectedStorey, ...affectedStoreysAbove];
    const buildSnapshot = (pos: Point2D) =>
      this.buildStoreysWithMovedPoint(
        allStoreys,
        originalPoint,
        pos,
        shapeIndex,
        input.pointIndex!,
      );

    const snapshot = buildSnapshot(position);
    const valid = this.isStoreySnapshotValid(snapshot, allStoreys);

    if (valid) {
      return undefined;
    }

    // Binary search for the largest safe drag ratio
    const origin = input.origin;
    let lo = 0;
    let hi = 1;

    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      const midPos: Point2D = [
        origin[0] + (position[0] - origin[0]) * mid,
        origin[1] + (position[1] - origin[1]) * mid,
      ];

      if (this.isStoreySnapshotValid(buildSnapshot(midPos), allStoreys)) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    if (lo < 1e-6) return input.origin;

    const safeRatio = Math.max(0, lo - 0.001);
    return [
      origin[0] + (position[0] - origin[0]) * safeRatio,
      origin[1] + (position[1] - origin[1]) * safeRatio,
    ];
  }

  /**
   * Enforce storey-above connectivity for multi-select drags.
   * Simulates linked-point propagation across all storeys above
   * and clamps via binary search when disconnection would occur.
   */
  private enforceStoreyAboveBoundaryMultiSelect(
    result: SnapResult,
    input: SnapInput,
    originalPoints: readonly Point2D[],
    excludedPointIndexes: ReadonlySet<number>,
  ): SnapResult {
    if (!input.shapeId) return result;

    const storeys = this.state.originalStoreys$.getValue();
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return result;

    const selectedStorey = storeys.find((s) => s.id === selectedStoreyId);
    if (!selectedStorey) return result;

    const shapeIndex = selectedStorey.shapes.findIndex(
      (s) => s.id === input.shapeId,
    );
    if (shapeIndex < 0) return result;

    const [dx, dy] = [
      result.position[0] - input.origin[0],
      result.position[1] - input.origin[1],
    ];
    if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return result;

    // Collect original positions of all selected (moving) points
    const movedOriginals: Point2D[] = [];
    for (const pi of excludedPointIndexes) {
      const p = originalPoints[pi];
      if (p) movedOriginals.push(p);
    }
    if (!movedOriginals.length) return result;

    // Only include storeys that have a point matching any moved original
    const affectedStoreysAbove = this.getStoreysAbove(
      storeys,
      selectedStorey,
    ).filter((s) =>
      movedOriginals.some((orig) => this.storeyHasPoint(s, orig)),
    );
    if (!affectedStoreysAbove.length) return result;

    const buildSnapshot = (ratio: number): Point2D[][][] => {
      const segmentPositions = input.isLineSegmentDrag
        ? resolveLineSegmentDragPositions(
            originalPoints,
            [...excludedPointIndexes],
            [dx * ratio, dy * ratio],
          )
        : undefined;
      const baseShapes = selectedStorey.shapes.map((shape, idx) => {
        if (idx !== shapeIndex) return shape.points;
        return originalPoints.map((p, pi): Point2D =>
          excludedPointIndexes.has(pi)
            ? (segmentPositions?.get(pi) ?? [
                p[0] + dx * ratio,
                p[1] + dy * ratio,
              ])
            : p,
        );
      });

      const upperSnapshots = affectedStoreysAbove.map((storey) =>
        storey.shapes.map((shape) =>
          shape.points.map((p): Point2D => {
            const movedIndex = movedOriginals.findIndex((orig) =>
              isEqualPoints(orig, p),
            );
            if (movedIndex < 0) return p;

            const originalIndex = [...excludedPointIndexes][movedIndex];
            return originalIndex !== undefined
              ? (segmentPositions?.get(originalIndex) ?? [
                  p[0] + dx * ratio,
                  p[1] + dy * ratio,
                ])
              : p;
          }),
        ),
      );

      return [baseShapes, ...upperSnapshots];
    };

    const allStoreys = [selectedStorey, ...affectedStoreysAbove];
    if (this.isStoreySnapshotValid(buildSnapshot(1), allStoreys)) return result;

    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      if (this.isStoreySnapshotValid(buildSnapshot(mid), allStoreys)) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    const safeRatio = Math.max(0, lo - 0.001);
    return {
      ...result,
      position: [
        input.origin[0] + (result.position[0] - input.origin[0]) * safeRatio,
        input.origin[1] + (result.position[1] - input.origin[1]) * safeRatio,
      ],
      snapped: true,
      boundaryIntersection: true,
    };
  }

  /**
   * Get all storeys above a given storey (including mezzanines).
   */
  private getStoreysAbove(storeys: Storey[], storey: Storey): Storey[] {
    const idx = getStoreyIndex(storeys, storey);
    if (idx < 0) return [];
    return storeys.slice(idx + 1);
  }

  /** Check if any shape in a storey contains a point at the given position */
  private storeyHasPoint(storey: Storey, point: Point2D): boolean {
    return storey.shapes.some((shape) =>
      shape.points.some((p) => isEqualPoints(p, point)),
    );
  }

  /**
   * Build a snapshot of storeys with a single point moved.
   * Simulates the same logic as {@link applyPointMoveUpdate}:
   * 1. Exact-position linked points are moved to newPosition
   * 2. Line-linked points (on edges adjacent to the moved point) are projected
   * @returns Array of storey shape point arrays (one per storey)
   */
  private buildStoreysWithMovedPoint(
    storeys: Storey[],
    originalPoint: Point2D,
    newPosition: Point2D,
    baseShapeIndex: number,
    basePointIndex: number,
  ): Point2D[][][] {
    const baseStorey = storeys[0];
    if (!baseStorey) return [];

    const baseShape = baseStorey.shapes[baseShapeIndex];
    if (!baseShape) return [];

    // Compute line-linked point positions using the real algorithm
    const lineLinked = getLineLinkedPointsAboveStorey({
      baseStoreys: storeys,
      storeyId: baseStorey.id,
      shapeId: baseShape.id,
      pointIndex: basePointIndex,
      nextPosition: newPosition,
    });

    // Build a lookup: storeyId:shapeId:pointIndex -> new position
    const lineLinkedMap = new Map<string, Point2D>();
    for (const lp of lineLinked) {
      lineLinkedMap.set(
        `${lp.storeyId}:${lp.shapeId}:${lp.pointIndex}`,
        lp.position,
      );
    }

    return storeys.map((storey, storeyIdx) =>
      storey.shapes.map((shape, shapeIdx) =>
        shape.points.map((p, pi): Point2D => {
          // Base storey shape: move the exact point index
          if (storeyIdx === 0 && shapeIdx === baseShapeIndex) {
            return pi === basePointIndex ? newPosition : p;
          }
          // Exact-position linked points
          if (isEqualPoints(p, originalPoint)) return newPosition;
          // Line-linked points
          const llKey = `${storey.id}:${shape.id}:${pi}`;
          return lineLinkedMap.get(llKey) ?? p;
        }),
      ),
    );
  }

  /**
   * Validate that a storey snapshot is geometrically valid.
   * Checks three constraints:
   * 1. No shape self-intersects (all storeys including base)
   * 2. No two shapes on the same storey overlap (all storeys)
   * 3. No solid overlaps a sibling building (DEV-2576)
   * 4. Every non-mezzanine storey connects to the non-mezzanine storey below
   * @param storeySnapshots Array of storey shape point arrays
   * @param storeys Original storey objects (for mezzanine detection)
   */
  private isStoreySnapshotValid(
    storeySnapshots: Point2D[][][],
    storeys: Storey[],
  ): boolean {
    // Self-intersection and overlap for all storeys (including index 0)
    for (let i = 0; i < storeySnapshots.length; i++) {
      const shapes = storeySnapshots[i];
      const storey = storeys[i];
      if (!shapes || !storey) continue;

      for (let s = 0; s < shapes.length; s++) {
        const shape = shapes[s];
        if (!shape || shape.length < 3) continue;

        if (isPolygonSelfIntersecting(shape)) return false;

        const sIsCutout = storey.shapes[s]?.cutout === true;
        for (let t = s + 1; t < shapes.length; t++) {
          const other = shapes[t];
          if (!other || other.length < 3) continue;
          const tIsCutout = storey.shapes[t]?.cutout === true;
          // Cutouts intentionally sit inside their enclosing solid —
          // only check overlap between shapes of the same kind.
          if (sIsCutout !== tIsCutout) continue;
          if (doPolygonsIntersect(shape, other)) return false;
        }

        if (
          !sIsCutout &&
          doesShapeIntersectBuildingSolids(
            { draft: true, points: shape },
            this.state.otherBuildings$.getValue(),
            getStoreyElevationRange(storeys, storey),
          )
        ) {
          return false;
        }
      }
    }

    // Connectivity: only between non-mezzanine consecutive pairs
    let lastNonMezzanineIdx = designGuards.is.mezzanineStorey(storeys[0])
      ? -1
      : 0;
    for (let i = 1; i < storeySnapshots.length; i++) {
      if (designGuards.is.mezzanineStorey(storeys[i])) continue;

      // No non-mezzanine storey found below yet — nothing to check against
      if (lastNonMezzanineIdx < 0) {
        lastNonMezzanineIdx = i;
        continue;
      }

      const below = storeySnapshots[lastNonMezzanineIdx];
      const above = storeySnapshots[i];
      if (!below || !above) {
        lastNonMezzanineIdx = i;
        continue;
      }

      const connected = above.every((abovePoints) =>
        below.some((belowPoints) =>
          doPolygonsIntersect(abovePoints, belowPoints),
        ),
      );
      if (!connected) return false;

      lastNonMezzanineIdx = i;
    }
    return true;
  }

  /**
   * Multi-select snap using only stable (pre-drag) geometry.
   * Used when dragging a line segment (two points) or multiple points.
   *
   * All positions are read from {@link originalStoreys$} to avoid feedback
   * loops with live storeys$ that already includes the previous frame's offset.
   *
   * Priority (aligned with single-point drag):
   *  1a. Storey below (endpoints) — if one of the segment endpoints would land
   *      on a point on the storey below, snap so that endpoint lands on it.
   *  1b. Storey below (grab point) — snap grab point to points/lines on the
   *      storey below.
   *  2. Angular snap — each selected point's non-selected neighbor emits
   *     angle lines; the best snap across all such anchors wins.
   *  3. Point-distance snap to a non-selected vertex
   */
  private snapMultiSelect(input: SnapInput): SnapResult {
    const noSnap: SnapResult = { position: input.position, snapped: false };
    const { shapeId, excludedPointIndexes } = input;
    if (!shapeId || !excludedPointIndexes?.size) return noSnap;

    const originalPoints = this.getOriginalShapePoints(shapeId);

    // Segment drag (exactly two endpoints): soft-constrain translation to
    // existing edge directions (sticky hysteresis). Stronger snaps may still win.
    const directionSoft =
      excludedPointIndexes.size === 2
        ? this.applyStickyDirectionSoftSnap(input)
        : undefined;
    const workingInput = directionSoft
      ? { ...input, position: directionSoft.position }
      : input;
    let result = noSnap;

    // 1a. When dragging a line: snap if one of its endpoints would land on a storey-below point
    if (!result.snapped && originalPoints && originalPoints.length >= 2) {
      const endpointSnap = this.snapToStoreyBelowForEndpoints(
        workingInput,
        originalPoints,
        excludedPointIndexes,
      );
      if (endpointSnap) result = endpointSnap;
    }

    // 1b. Storey-below: snap grab point to points/lines on the storey below
    if (!result.snapped) {
      const storeyBelowResult = this.snapToStoreyBelow(workingInput);
      if (storeyBelowResult) result = storeyBelowResult;
    }

    if (originalPoints && originalPoints.length >= 3) {
      const total = originalPoints.length;

      // 2. Angular snap from non-selected neighbors using original geometry.
      if (!result.snapped && this.config.enableAngularSnap) {
        const angleResult = this.snapMultiSelectAngle(
          workingInput,
          originalPoints,
          total,
        );
        if (angleResult) {
          result = {
            position: angleResult.position,
            snapped: true,
            snapType: SnapCandidateType.Angle,
            snapAngle: angleResult.angle,
          };
        }
      }

      // 3. Point-distance snap to non-selected vertices
      if (!result.snapped && this.config.enablePointSnap) {
        const stableSnap = this.snapToStablePoint(workingInput);
        if (stableSnap) {
          result = {
            position: stableSnap.position,
            snapped: true,
            snapType: SnapCandidateType.Point,
          };
        }
      }
    }

    // Direction soft-snap is the fallback when nothing stronger latched.
    if (!result.snapped && directionSoft) {
      result = directionSoft;
    }

    // 4. Boundary enforcement
    if (this.config.enableBoundary && originalPoints) {
      const isMezzanine = Boolean(this.getSelectedMezzanineParentShapes());

      // For mezzanine storeys: skip edge-intersection boundary enforcement.
      // Mezzanine shapes share vertices with parent boundaries which causes
      // false-positive intersection detections. Real-time clamping is handled
      // in applyPointMoveUpdate via clampMezzanineStoreysInStorey instead.
      if (!isMezzanine) {
        result = this.applyMultiSelectBoundary(
          result,
          workingInput,
          originalPoints,
          excludedPointIndexes,
        );
      }

      // 5. Prevent invalid geometry on storeys above (self-intersection,
      //    overlap, disconnection). Applies to both regular and mezzanine.
      result = this.enforceStoreyAboveBoundaryMultiSelect(
        result,
        workingInput,
        originalPoints,
        excludedPointIndexes,
      );

      return result;
    }
    return result;
  }

  /**
   * Soft-snap a two-point segment drag onto directions of existing storey edges.
   * Sticky until the pointer breaks out farther than the enter radius.
   */
  private applyStickyDirectionSoftSnap(
    input: SnapInput,
  ): SnapResult | undefined {
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return undefined;

    const originalStoreys = this.state.originalStoreys$.getValue();
    const originalStorey = originalStoreys.find(
      (s) => s.id === selectedStoreyId,
    );
    if (!originalStorey?.shapes?.length) return undefined;

    const directions = collectBidirectionalEdgeAnglesRadians(
      originalStorey.shapes,
    );
    const enterDistanceMeters = this.getSnapDistance('angle');
    const breakoutDistanceMeters =
      enterDistanceMeters * DIRECTION_SOFT_SNAP_BREAKOUT_FACTOR;

    const soft = softSnapOffsetToDirections(
      input.origin,
      input.position,
      directions,
      {
        enterDistanceMeters,
        breakoutDistanceMeters,
        sticky: this.stickyDirectionSnap,
      },
    );
    this.stickyDirectionSnap = soft.state;
    if (!soft.snapped || soft.angleRadians === undefined) return undefined;

    return {
      position: soft.position,
      snapped: true,
      snapType: SnapCandidateType.Angle,
      snapAngle: soft.angleRadians * (180 / Math.PI),
    };
  }

  /**
   * Apply boundary enforcement to a multi-select drag result.
   * Builds all edges of the shape after applying the drag offset to selected
   * points and checks each against static (non-moving) edges on the storey.
   * If any intersection is found, the drag is clamped via binary search.
   *
   * All geometry reads use {@link originalStoreys$} to stay deterministic.
   */
  private applyMultiSelectBoundary(
    result: SnapResult,
    input: SnapInput,
    originalPoints: readonly Point2D[],
    excludedPointIndexes: ReadonlySet<number>,
  ): SnapResult {
    if (!this.config.enableBoundary) return result;
    if (!input.shapeId || originalPoints.length < 3) return result;

    const total = originalPoints.length;
    const [dx, dy] = [
      result.position[0] - input.origin[0],
      result.position[1] - input.origin[1],
    ];
    if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return result;

    // Static edges: edges in originalStoreys$ where neither endpoint is selected
    const staticEdges = this.getOriginalStoreyEdgesExcluding(
      input.shapeId,
      excludedPointIndexes,
    );
    if (staticEdges.length === 0) return result;

    // Build the full set of moved edges: every edge of the shape where at
    // least one endpoint is a selected point gets its geometry recomputed.
    const movedEdges = this.buildMovedEdges(
      originalPoints,
      total,
      excludedPointIndexes,
      dx,
      dy,
      input.isLineSegmentDrag,
    );
    if (movedEdges.length === 0) return result;

    // Quick check: does the full offset cause any intersection?
    if (!this.anyEdgeCrossesStatic(movedEdges, staticEdges)) return result;

    // Binary search for the largest safe ratio
    const safeRatio = this.binarySearchSafeRatio(
      originalPoints,
      total,
      excludedPointIndexes,
      staticEdges,
      dx,
      dy,
      input.isLineSegmentDrag,
    );

    const clampedPosition: Point2D = [
      input.origin[0] + (result.position[0] - input.origin[0]) * safeRatio,
      input.origin[1] + (result.position[1] - input.origin[1]) * safeRatio,
    ];

    return {
      ...result,
      position: clampedPosition,
      snapped: true,
      boundaryIntersection: true,
    };
  }

  /**
   * Build all edges of the shape that have at least one selected endpoint,
   * with selected points offset by (dx, dy).
   */
  private buildMovedEdges(
    originalPoints: readonly Point2D[],
    total: number,
    excludedPointIndexes: ReadonlySet<number>,
    dx: number,
    dy: number,
    isLineSegmentDrag = false,
  ): Line2D[] {
    const edges: Line2D[] = [];
    const segmentPositions = isLineSegmentDrag
      ? resolveLineSegmentDragPositions(
          originalPoints,
          [...excludedPointIndexes],
          [dx, dy],
        )
      : undefined;
    if (isLineSegmentDrag && !segmentPositions) return edges;

    for (let i = 0; i < total; i++) {
      const j = (i + 1) % total;
      const iSelected = excludedPointIndexes.has(i);
      const jSelected = excludedPointIndexes.has(j);
      if (!iSelected && !jSelected) continue;

      const origI = originalPoints[i];
      const origJ = originalPoints[j];
      if (!origI || !origJ) continue;

      const posI: Point2D = iSelected
        ? (segmentPositions?.get(i) ?? [origI[0] + dx, origI[1] + dy])
        : origI;
      const posJ: Point2D = jSelected
        ? (segmentPositions?.get(j) ?? [origJ[0] + dx, origJ[1] + dy])
        : origJ;

      edges.push([posI, posJ]);
    }

    return edges;
  }

  /**
   * Check if any of the moved edges intersect any static edge.
   */
  private anyEdgeCrossesStatic(
    movedEdges: Line2D[],
    staticEdges: { edge: Line2D; shapeId: string }[],
  ): boolean {
    for (const movedEdge of movedEdges) {
      for (const { edge: staticEdge } of staticEdges) {
        const hit = findIntersectionPoint(movedEdge, staticEdge);
        if (!hit) continue;
        if (this.isAtEndpointOfEdge(hit, movedEdge)) continue;
        if (this.isAtEndpointOfEdge(hit, staticEdge)) continue;
        return true;
      }
    }
    return false;
  }

  /**
   * Binary search for the largest drag ratio t in [0, 1] that does not
   * cause any moved edge to cross a static edge.
   */
  private binarySearchSafeRatio(
    originalPoints: readonly Point2D[],
    total: number,
    excludedPointIndexes: ReadonlySet<number>,
    staticEdges: { edge: Line2D; shapeId: string }[],
    dx: number,
    dy: number,
    isLineSegmentDrag = false,
  ): number {
    let lo = 0;
    let hi = 1;

    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      const edges = this.buildMovedEdges(
        originalPoints,
        total,
        excludedPointIndexes,
        dx * mid,
        dy * mid,
        isLineSegmentDrag,
      );
      if (this.anyEdgeCrossesStatic(edges, staticEdges)) {
        hi = mid;
      } else {
        lo = mid;
      }
    }

    // Pull back slightly from the boundary to avoid landing exactly on the edge
    return Math.max(0, lo - 0.001);
  }

  /**
   * Get edges from the original (pre-drag) storey geometry, excluding edges
   * where either endpoint is one of the selected (moving) point indexes.
   *
   * Same cutout/solid filtering as {@link getStoreyEdges}: when dragging a
   * solid, cutout edges act as boundaries; when dragging a cutout, solid
   * edges are excluded.
   */
  private getOriginalStoreyEdgesExcluding(
    shapeId: string,
    excludedIndexes: ReadonlySet<number>,
  ): { edge: Line2D; shapeId: string }[] {
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return [];

    const storeys = this.state.originalStoreys$.getValue();
    const selectedStorey = storeys.find((s) => s.id === selectedStoreyId);

    const edges: { edge: Line2D; shapeId: string }[] = [];

    if (selectedStorey?.shapes?.length) {
      const draggedShape = selectedStorey.shapes.find((s) => s.id === shapeId);
      const draggedIsCutout = draggedShape?.cutout === true;

      for (const shape of selectedStorey.shapes) {
        const shapeIsCutout = shape.cutout === true;

        // Cutout drag: skip solid edges
        if (draggedIsCutout && !shapeIsCutout) continue;

        const points = shape.points ?? [];
        if (points.length < 2) continue;

        const total = points.length;
        for (let i = 0; i < total; i++) {
          const startIndex = i;
          const endIndex = (i + 1) % total;

          // Skip edges touching any moving point in the same shape
          if (shape.id === shapeId) {
            if (
              excludedIndexes.has(startIndex) ||
              excludedIndexes.has(endIndex)
            ) {
              continue;
            }
          }

          const start = points[startIndex];
          const end = points[endIndex];
          if (!start || !end) continue;

          edges.push({ edge: [start, end], shapeId: shape.id });
        }
      }
    }

    edges.push(...this.getOtherBuildingBoundaryEdges());
    return edges;
  }

  /**
   * Check if a point is at either endpoint of an edge (within 1mm tolerance).
   */
  private isAtEndpointOfEdge(point: Point2D, edge: Line2D): boolean {
    return this.isAtOrigin(point, edge[0]) || this.isAtOrigin(point, edge[1]);
  }

  /**
   * Angular snap for multi-select drags.
   *
   * Instead of projecting the mouse position, this projects each selected
   * point's *expected* position (`originalPoint + currentOffset`) against
   * angle lines from its non-selected neighbor. When a match is found the
   * offset is adjusted so that specific point lands on the angle line, and
   * the adjusted offset is translated back to a snap position for the
   * mouse-origin coordinate system.
   *
   * All point reads go through {@link originalStoreys$} to keep the snap
   * deterministic across frames.
   */
  private snapMultiSelectAngle(
    input: SnapInput,
    originalPoints: readonly Point2D[],
    total: number,
  ): { position: Point2D; angle: number } | undefined {
    const { excludedPointIndexes } = input;
    if (!excludedPointIndexes) return undefined;

    // Current drag offset in metric space
    const [dx, dy] = [
      input.position[0] - input.origin[0],
      input.position[1] - input.origin[1],
    ];

    type AngleAnchor = {
      selectedIdx: number;
      anchor: Point2D;
      refAngle: number;
    };

    // Collect anchors: for each selected point, find non-selected neighbors
    // and the reference angle from the segment leading into that anchor.
    const anchors: AngleAnchor[] = [];

    for (const selectedIdx of excludedPointIndexes) {
      const prevIdx = (selectedIdx - 1 + total) % total;
      const nextIdx = (selectedIdx + 1) % total;

      for (const neighborIdx of [prevIdx, nextIdx]) {
        if (excludedPointIndexes.has(neighborIdx)) continue;
        const anchor = originalPoints[neighborIdx];
        if (!anchor) continue;

        const outerIdx = this.findOuterNeighborIndex(
          neighborIdx,
          excludedPointIndexes,
          total,
        );
        const outerPoint =
          outerIdx !== undefined ? originalPoints[outerIdx] : undefined;
        if (!outerPoint) continue;

        anchors.push({
          selectedIdx,
          anchor,
          refAngle: this.getSegmentAngle(outerPoint, anchor),
        });
      }
    }

    if (anchors.length === 0) return undefined;

    // Try intersection snap when we have anchors from 2 different anchor points
    if (anchors.length >= 2) {
      const intersectionResult = this.tryMultiSelectIntersection(
        input,
        originalPoints,
        anchors,
        dx,
        dy,
      );
      if (intersectionResult) return intersectionResult;
    }

    // Single-line projection: test each selected point against its anchor's
    // angle lines and pick the closest match.
    let best:
      { position: Point2D; angle: number; distance: number } | undefined;

    for (const { selectedIdx, anchor, refAngle } of anchors) {
      const origPoint = originalPoints[selectedIdx];
      if (!origPoint) continue;

      // Where this selected point would end up with the current offset
      const expectedPos: Point2D = [origPoint[0] + dx, origPoint[1] + dy];

      for (const relAngle of SNAP_ANGLES) {
        const absAngle = refAngle + relAngle;
        const projected = this.projectOntoAngleLine(
          expectedPos,
          anchor,
          absAngle,
        );

        const distance = getDistance(expectedPos, projected);
        const weight = getAngleWeight(relAngle);
        const weightedDistance = distance * weight;

        if (distance < this.getSnapDistance('angle')) {
          if (!best || weightedDistance < best.distance) {
            // Translate back: the adjusted offset for this point is
            // projected - origPoint. The corresponding mouse position is
            // origin + adjustedOffset.
            const snappedMousePos: Point2D = [
              input.origin[0] + (projected[0] - origPoint[0]),
              input.origin[1] + (projected[1] - origPoint[1]),
            ];
            best = {
              position: snappedMousePos,
              angle: this.normalizeAngle(absAngle),
              distance: weightedDistance,
            };
          }
        }
      }
    }

    return best ? { position: best.position, angle: best.angle } : undefined;
  }

  /**
   * Try to find a snap position where two selected points simultaneously
   * satisfy angle constraints from their respective non-selected anchors.
   */
  private tryMultiSelectIntersection(
    input: SnapInput,
    originalPoints: readonly Point2D[],
    anchors: { selectedIdx: number; anchor: Point2D; refAngle: number }[],
    dx: number,
    dy: number,
  ): { position: Point2D; angle: number } | undefined {
    // Group anchors by their anchor point position
    const anchorGroups = new Map<string, typeof anchors>();
    for (const a of anchors) {
      const key = `${a.anchor[0]},${a.anchor[1]}`;
      const group = anchorGroups.get(key) ?? [];
      group.push(a);
      anchorGroups.set(key, group);
    }

    const groups = [...anchorGroups.values()];
    if (groups.length < 2) return undefined;

    // Try pairwise: take one anchor from each group, build angle lines,
    // find intersections, then translate back.
    for (let a = 0; a < groups.length - 1; a++) {
      for (let b = a + 1; b < groups.length; b++) {
        const reprA = groups[a]![0]!;
        const reprB = groups[b]![0]!;

        const origA = originalPoints[reprA.selectedIdx];
        const origB = originalPoints[reprB.selectedIdx];
        if (!origA || !origB) continue;

        const expectedA: Point2D = [origA[0] + dx, origA[1] + dy];

        const linesA: AngleLine[] = SNAP_ANGLES.map((rel) => ({
          anchor: reprA.anchor,
          angle: reprA.refAngle + rel,
          relAngle: rel,
        }));
        const linesB: AngleLine[] = SNAP_ANGLES.map((rel) => ({
          anchor: reprB.anchor,
          angle: reprB.refAngle + rel,
          relAngle: rel,
        }));

        const intersection = this.findBestAngleIntersection(
          expectedA,
          linesA,
          linesB,
        );

        if (intersection) {
          // intersection.position is where selectedIdx A should land.
          // Translate back to mouse coordinate space.
          const snappedMousePos: Point2D = [
            input.origin[0] + (intersection.position[0] - origA[0]),
            input.origin[1] + (intersection.position[1] - origA[1]),
          ];
          return { position: snappedMousePos, angle: intersection.angle };
        }
      }
    }

    return undefined;
  }

  /**
   * Walk away from the selected group to find the anchor's "outer" neighbor.
   * Starting from {@link anchorIdx}, move in the direction away from the
   * selected set to find the first non-selected index.
   */
  private findOuterNeighborIndex(
    anchorIdx: number,
    excluded: ReadonlySet<number>,
    total: number,
  ): number | undefined {
    // The anchor is a non-selected neighbor of some selected point.
    // Its two neighbors are: anchorIdx-1 and anchorIdx+1.
    // One of them is selected (that's how we found this anchor).
    // The other is the "outer" neighbor we want.
    const prev = (anchorIdx - 1 + total) % total;
    const next = (anchorIdx + 1) % total;
    if (!excluded.has(prev)) return prev;
    if (!excluded.has(next)) return next;
    return undefined;
  }

  /**
   * Read shape points from the stable pre-drag snapshot.
   */
  private getOriginalShapePoints(
    shapeId: string,
  ): readonly Point2D[] | undefined {
    const originalStoreys = this.state.originalStoreys$.getValue();
    for (const storey of originalStoreys) {
      const shape = storey.shapes?.find((s) => s.id === shapeId);
      if (shape?.points) return shape.points;
    }
    return undefined;
  }

  /**
   * Snap an existing point to delete-point targets within the same shape.
   * @param input {@link SnapInput} for the move operation
   */
  private snapToDeletePoint(
    input: SnapInput,
  ): { position: Point2D } | undefined {
    const { shapeId, pointIndex } = input;
    if (!shapeId || pointIndex === undefined) return undefined;

    const shapePoints = this.getShapePoints(shapeId, input.isDraft);
    if (!shapePoints || shapePoints.length < 2) return undefined;

    const neighborIndexes = this.getDeleteNeighborIndexes(
      shapePoints.length,
      pointIndex,
      input.isDraft,
    );
    if (neighborIndexes.length === 0) return undefined;

    let closest: Point2D | undefined;
    let closestDistance = this.getSnapDistance('deletePoint');

    for (const idx of neighborIndexes) {
      // Skip neighbors that are co-moving with the dragged point
      if (input.excludedPointIndexes?.has(idx)) continue;
      const candidate = shapePoints[idx];
      if (!candidate) continue;
      const distance = getDistance(input.position, candidate);
      if (distance < closestDistance) {
        closest = candidate;
        closestDistance = distance;
      }
    }

    return closest ? { position: closest } : undefined;
  }

  /**
   * Resolve neighbor indexes used for delete-point snapping.
   * @param totalPoints Total points in the shape
   * @param pointIndex Index of the moved point
   * @param isDraft Whether the shape is a draft (open polyline)
   */
  private getDeleteNeighborIndexes(
    totalPoints: number,
    pointIndex: number,
    isDraft?: boolean,
  ): number[] {
    if (totalPoints < 2) return [];

    const candidates = new Set<number>();
    const prevIndex = pointIndex - 1;
    const nextIndex = pointIndex + 1;

    // For closed shapes, wrap indices so endpoints are neighbors.
    if (!isDraft && totalPoints >= 3) {
      candidates.add((prevIndex + totalPoints) % totalPoints);
      candidates.add(nextIndex % totalPoints);
    } else {
      if (prevIndex >= 0) candidates.add(prevIndex);
      if (nextIndex < totalPoints) candidates.add(nextIndex);
    }

    candidates.delete(pointIndex);
    return [...candidates];
  }

  /**
   * Check if a position is at the origin (within a small tolerance).
   */
  private isAtOrigin(position: Point2D, origin: Point2D): boolean {
    const tolerance = 0.001; // 1mm tolerance
    return getDistance(position, origin) < tolerance;
  }

  /**
   * Get neighbor points for a given point index (excluding the point itself).
   */
  private getNeighborPoints(
    points: readonly Point2D[],
    pointIndex: number,
    isDraft?: boolean,
  ): Point2D[] {
    const neighbors: Point2D[] = [];
    const total = points.length;

    if (total < 2) return neighbors;

    // Previous neighbor
    if (pointIndex > 0) {
      const prev = points[pointIndex - 1];
      if (prev) neighbors.push(prev);
    } else if (!isDraft && total >= 3) {
      // Wrap around for closed shapes
      const prev = points[total - 1];
      if (prev) neighbors.push(prev);
    }

    // Next neighbor
    if (pointIndex < total - 1) {
      const next = points[pointIndex + 1];
      if (next) neighbors.push(next);
    } else if (!isDraft && total >= 3) {
      // Wrap around for closed shapes
      const next = points[0];
      if (next) neighbors.push(next);
    }

    return neighbors;
  }

  /**
   * Snap to angular lines (0°, 90°) relative to adjacent segments.
   * For existing shapes: uses both previous and next segment directions.
   * For drafts: only uses previous segment direction.
   */
  private snapToAngle(
    input: SnapInput,
  ): { position: Point2D; angle: number } | undefined {
    const { shapeId, pointIndex, isDraft } = input;
    if (pointIndex === undefined || shapeId === undefined) return undefined;

    // Get the shape's points
    const shapePoints = this.getShapePoints(shapeId, isDraft);
    if (!shapePoints || shapePoints.length < 2) return undefined;

    const rightAngleSnap = this.snapToRightAngleWithNeighbors(
      shapePoints,
      pointIndex,
      input.position,
      isDraft,
    );
    if (rightAngleSnap) return rightAngleSnap;

    const anchorPoints = this.getAngularAnchorPoints(
      shapePoints,
      pointIndex,
      isDraft,
    );
    if (anchorPoints.length === 0) return undefined;

    if (!isDraft && anchorPoints.length >= 2) {
      const [firstAnchor, secondAnchor] = anchorPoints;
      if (firstAnchor && secondAnchor) {
        // Prioritize the intersection of the two angle families for existing points.
        const linesA: AngleLine[] = SNAP_ANGLES.map((relAngle) => ({
          anchor: firstAnchor.point,
          angle: firstAnchor.referenceAngle + relAngle,
          relAngle,
        }));
        const linesB: AngleLine[] = SNAP_ANGLES.map((relAngle) => ({
          anchor: secondAnchor.point,
          angle: secondAnchor.referenceAngle + relAngle,
          relAngle,
        }));
        const intersection = this.findBestAngleIntersection(
          input.position,
          linesA,
          linesB,
        );
        if (intersection) {
          return intersection;
        }
      }
    }

    let closest:
      { position: Point2D; angle: number; distance: number } | undefined;

    // Get neighbor points to exclude from snap targets
    const neighborPoints = this.getNeighborPoints(
      shapePoints,
      pointIndex,
      isDraft,
    );

    for (const anchor of anchorPoints) {
      // For each snap angle relative to the reference segment
      for (const relativeAngle of SNAP_ANGLES) {
        const absoluteAngle = anchor.referenceAngle + relativeAngle;
        const projected = this.projectOntoAngleLine(
          input.position,
          anchor.point,
          absoluteAngle,
        );

        // Skip if projected point is at origin or at a neighbor point
        if (this.isAtOrigin(projected, input.origin)) continue;
        if (neighborPoints.some((np) => this.isAtOrigin(projected, np))) {
          continue;
        }

        const distance = getDistance(input.position, projected);
        const weight = getAngleWeight(relativeAngle);
        const weightedDistance = distance * weight;

        if (distance < this.getSnapDistance('angle')) {
          if (!closest || weightedDistance < closest.distance) {
            closest = {
              position: projected,
              angle: this.normalizeAngle(absoluteAngle),
              distance: weightedDistance, // Store weighted distance for comparison
            };
          }
        }
      }
    }

    return closest
      ? { position: closest.position, angle: closest.angle }
      : undefined;
  }

  /**
   * Snap to the closest point that forms a 90-degree angle with neighbors.
   * @param points Shape points
   * @param pointIndex Index of moved point
   * @param position Current position
   * @param isDraft Whether the shape is a draft (open polyline)
   */
  private snapToRightAngleWithNeighbors(
    points: readonly Point2D[],
    pointIndex: number,
    position: Point2D,
    isDraft?: boolean,
  ): { position: Point2D; angle: number } | undefined {
    const neighbors = this.getAdjacentPoints(points, pointIndex, isDraft);
    if (!neighbors) return undefined;

    const { prev, next } = neighbors;
    const radius = getDistance(prev, next) / 2;
    if (radius < 0.001) return undefined;

    const center: Point2D = [(prev[0] + next[0]) / 2, (prev[1] + next[1]) / 2];
    const centerDistance = getDistance(center, position);
    if (centerDistance < 0.001) return undefined;

    const distanceToCircle = Math.abs(centerDistance - radius);
    if (distanceToCircle > this.getSnapDistance('angle')) return undefined;

    const scale = radius / centerDistance;
    const candidate: Point2D = [
      center[0] + (position[0] - center[0]) * scale,
      center[1] + (position[1] - center[1]) * scale,
    ];

    return {
      position: candidate,
      angle: this.normalizeAngle(this.getSegmentAngle(prev, candidate)),
    };
  }

  /**
   * Get anchor points for angular snapping based on adjacent segments.
   */
  private getAngularAnchorPoints(
    points: readonly Point2D[],
    pointIndex: number,
    isDraft?: boolean,
  ): { point: Point2D; referenceAngle: number }[] {
    const anchors: { point: Point2D; referenceAngle: number }[] = [];
    const total = points.length;

    // Previous segment: from point[i-1] to point[i] (anchor at point[i-1])
    // Reference angle is the direction of the segment BEFORE the moved point
    if (pointIndex >= 1) {
      const prevPoint = points[pointIndex - 1];
      if (prevPoint) {
        // If there's a point before prevPoint, use that segment's angle
        if (pointIndex >= 2) {
          const prevPrevPoint = points[pointIndex - 2];
          if (prevPrevPoint) {
            const angle = this.getSegmentAngle(prevPrevPoint, prevPoint);
            anchors.push({ point: prevPoint, referenceAngle: angle });
          }
        } else if (!isDraft && total >= 3) {
          // For closed shapes, wrap around
          const prevPrevPoint = points[(pointIndex - 2 + total) % total];
          if (prevPrevPoint) {
            const angle = this.getSegmentAngle(prevPrevPoint, prevPoint);
            anchors.push({ point: prevPoint, referenceAngle: angle });
          }
        }
      }
    }

    // Next segment: from point[i+1] to point[i+2] (anchor at point[i+1])
    // Only for closed shapes, not drafts
    if (!isDraft && total >= 3) {
      const nextIndex = (pointIndex + 1) % total;
      const nextNextIndex = (pointIndex + 2) % total;
      const nextPoint = points[nextIndex];
      const nextNextPoint = points[nextNextIndex];

      if (nextPoint && nextNextPoint) {
        const angle = this.getSegmentAngle(nextPoint, nextNextPoint);
        anchors.push({ point: nextPoint, referenceAngle: angle });
      }
    }

    return anchors;
  }

  /**
   * Get adjacent points (prev and next) for a point index.
   * @param points Shape points
   * @param pointIndex Index of moved point
   * @param isDraft Whether the shape is a draft (open polyline)
   */
  private getAdjacentPoints(
    points: readonly Point2D[],
    pointIndex: number,
    isDraft?: boolean,
  ): { prev: Point2D; next: Point2D } | undefined {
    const total = points.length;
    if (total < 2) return undefined;

    const prevIndex =
      pointIndex > 0
        ? pointIndex - 1
        : !isDraft && total >= 3
          ? total - 1
          : undefined;
    const nextIndex =
      pointIndex < total - 1
        ? pointIndex + 1
        : !isDraft && total >= 3
          ? 0
          : undefined;

    if (prevIndex === undefined || nextIndex === undefined) return undefined;

    const prev = points[prevIndex];
    const next = points[nextIndex];
    if (!prev || !next) return undefined;

    return { prev, next };
  }

  /**
   * Get points for a shape by id.
   */
  private getShapePoints(
    shapeId: string,
    isDraft?: boolean,
  ): readonly Point2D[] | undefined {
    if (isDraft) {
      const draftShape = this.state.draftShape$.getValue();
      return draftShape?.id === shapeId ? draftShape.points : undefined;
    }

    // Try current storeys first, then fall back to original storeys
    const storeySources = [
      this.state.storeys$.getValue(),
      this.state.originalStoreys$.getValue(),
    ];

    for (const storeys of storeySources) {
      for (const storey of storeys) {
        const shape = storey.shapes?.find((s) => s.id === shapeId);
        if (shape?.points) return shape.points;
      }
    }
    return undefined;
  }

  /**
   * Calculate the angle of a line segment in degrees.
   */
  private getSegmentAngle(from: Point2D, to: Point2D): number {
    const [x1, y1] = from;
    const [x2, y2] = to;
    const radians = Math.atan2(y2 - y1, x2 - x1);
    return radians * (180 / Math.PI);
  }

  /**
   * Project a point onto an infinite line defined by anchor and angle.
   */
  private projectOntoAngleLine(
    point: Point2D,
    anchor: Point2D,
    angleDegrees: number,
  ): Point2D {
    const angleRadians = angleDegrees * (Math.PI / 180);
    const [px, py] = point;
    const [ax, ay] = anchor;

    // Direction vector of the line
    const dx = Math.cos(angleRadians);
    const dy = Math.sin(angleRadians);

    // Vector from anchor to point
    const apx = px - ax;
    const apy = py - ay;

    // Project onto line direction
    const t = apx * dx + apy * dy;

    return [ax + t * dx, ay + t * dy];
  }

  /**
   * Calculate the perpendicular distance from a point to an angle line.
   * @param point {@link Point2D} to measure from
   * @param anchor {@link Point2D} anchor of the line
   * @param angleDegrees Angle of the line in degrees
   */
  private getDistanceToAngleLine(
    point: Point2D,
    anchor: Point2D,
    angleDegrees: number,
  ): number {
    const projection = this.projectOntoAngleLine(point, anchor, angleDegrees);
    return getDistance(point, projection);
  }

  /**
   * Normalize angle to 0-360 range.
   */
  private normalizeAngle(angle: number): number {
    let normalized = angle % 360;
    if (normalized < 0) normalized += 360;
    return normalized;
  }

  /**
   * Check if a snap relative angle is orthogonal (0°, 90°, 180°, 270°).
   * @param relativeAngle {@link AngleLine} relative angle
   */
  private isOrthogonalSnapAngle(relativeAngle: number): boolean {
    const normalized = ((relativeAngle % 360) + 360) % 360;
    return normalized % 90 === 0;
  }

  /**
   * Find the closest point on any line candidate.
   */
  private snapToLine(input: SnapInput): { position: Point2D } | undefined {
    let closest: Point2D | undefined;
    let closestDistance = this.getSnapDistance('line');

    for (const candidate of this.candidates) {
      if (candidate.type !== SnapCandidateType.Line) continue;

      // Skip lines from the same shape being moved
      if (candidate.shapeId === input.shapeId) continue;

      const [start, end] = candidate.line;
      const projection = this.projectPointOntoSegment(
        input.position,
        start,
        end,
      );
      if (!projection) continue;

      const distance = getDistance(input.position, projection);
      if (distance < closestDistance) {
        closest = projection;
        closestDistance = distance;
      }
    }

    return closest ? { position: closest } : undefined;
  }

  /**
   * Project a point onto a line segment.
   * Returns the closest point on the segment, or undefined if projection falls outside.
   */
  private projectPointOntoSegment(
    point: Point2D,
    segmentStart: Point2D,
    segmentEnd: Point2D,
  ): Point2D | undefined {
    const [px, py] = point;
    const [ax, ay] = segmentStart;
    const [bx, by] = segmentEnd;

    const abx = bx - ax;
    const aby = by - ay;
    const apx = px - ax;
    const apy = py - ay;

    const abLengthSquared = abx * abx + aby * aby;
    if (abLengthSquared === 0) return undefined;

    // Calculate projection ratio
    const t = (apx * abx + apy * aby) / abLengthSquared;

    // Clamp t to [0, 1] to stay within segment
    if (t < 0 || t > 1) return undefined;

    const projectedX = ax + t * abx;
    const projectedY = ay + t * aby;

    return [projectedX, projectedY];
  }

  /**
   * Get all current snap candidates (for debugging/visualization).
   */
  getCandidates(): readonly SnapCandidate[] {
    return this.candidates;
  }

  /**
   * Get the current snap configuration.
   */
  getConfig(): Readonly<SnapConfig> {
    return this.config;
  }

  /**
   * Update snap configuration at runtime.
   */
  updateConfig(updates: Partial<SnapConfig>): void {
    Object.assign(this.config, updates);
  }

  /**
   * Dispose the snap handler and unsubscribe from state.
   */
  dispose(): void {
    this.subscription.unsubscribe();
    this.candidates = [];
  }
}
